import React, { useRef, useEffect, useImperativeHandle, forwardRef, useMemo, useCallback, useState } from "react";
import { createChart, IChartApi, ISeriesApi, UTCTimestamp, LineData, WhitespaceData } from "lightweight-charts";
// import { calculateRSI, calculateMACD } from "../Indicators";
import RectangleTool, { RectangleToolRef } from '../Tools/RectangleTool';
import PointTool, { PointToolRef } from '../Tools/PointTool';
import { Button, Space, Tooltip, Popover, InputNumber, Dropdown } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { BorderOuterOutlined, AimOutlined, ClearOutlined, DeleteOutlined, SettingOutlined, SlidersOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import { formatTimestampToEST } from '../../utils/timeUtils';
import { logger } from '../../utils/logger';
import { IndicatorId, IndicatorPlacement } from '../../constants/indicators';
import {
  RSI as TechnicalRSI,
  MACD as TechnicalMACD,
  SMA as TechnicalSMA,
  EMA as TechnicalEMA,
  WMA as TechnicalWMA,
  WEMA as TechnicalWEMA,
  OBV as TechnicalOBV,
  BollingerBands as TechnicalBollingerBands,
  WilliamsR as TechnicalWilliamsR,
  ATR as TechnicalATR,
  MFI as TechnicalMFI,
  Stochastic as TechnicalStochastic,
  ROC as TechnicalROC,
  TRIX as TechnicalTRIX,
  VWAP as TechnicalVWAP,
  ADL as TechnicalADL,
  ADX as TechnicalADX,
  AwesomeOscillator as TechnicalAO,
  CCI as TechnicalCCI,
  ForceIndex as TechnicalForceIndex,
  KST as TechnicalKST,
  PSAR as TechnicalPSAR,
  StochasticRSI as TechnicalStochasticRSI,
  VolumeProfile as TechnicalVolumeProfile,
  IchimokuCloud as TechnicalIchimokuCloud,
} from 'technicalindicators';

interface MultiPaneChartProps {
  ohlcData: any[];
  interval?: string;
  width?: number;
  mainHeight?: number;
  subHeight?: number;
  onRectChange?: (rect: any) => void;
  onPointsChange?: (points: { time: number; price: number }[]) => void;
  lastUpdateType?: 'initial' | 'newCandle' | 'updateCandle';
  lastUpdatedCandle?: any;
  hideToolbar?: boolean;
  showVolume?: boolean;
  showRsi?: boolean;
  showMacd?: boolean;
  showSma?: boolean;
  showEma?: boolean;
  showWma?: boolean;
  showWema?: boolean;
  showTypicalPrice?: boolean;
  showObv?: boolean;
  showBollingerBands?: boolean;
  showWilliamsR?: boolean;
  showAtr?: boolean;
  showMfi?: boolean;
  showStochastic?: boolean;
  showRoc?: boolean;
  showTrix?: boolean;
  showVwap?: boolean;
  showAdl?: boolean;
  showAdx?: boolean;
  showAo?: boolean;
  showCci?: boolean;
  showForceIndex?: boolean;
  showKst?: boolean;
  showPsar?: boolean;
  showStochRsi?: boolean;
  showVolumeProfile?: boolean;
  showIchimokuCloud?: boolean;
  indicatorPlacements?: Record<IndicatorId, IndicatorPlacement>;
  onIndicatorToggle?: (indicator: IndicatorId) => void;
  onPlacementChange?: (indicator: IndicatorId, placement: IndicatorPlacement) => void;
}

export interface TradeMarker {
  time: number;  // Unix timestamp in seconds
  type: 'buy' | 'sell';
  price: number;
  label?: string;
}

export interface MultiPaneChartRef {
  getCanvas: () => HTMLCanvasElement | null;
  getChartApi?: () => IChartApi | null;
  getCandleSeries?: () => ISeriesApi<'Candlestick'> | null;
  toggleRectangleTool: () => void;
  togglePointTool: () => void;
  clearRectangle: () => void;
  clearPoints: () => void;
  setRectangleSelection: (rect: { startIndex: number; endIndex: number }) => void;
  setTradeMarkers: (markers: TradeMarker[]) => void;
  clearTradeMarkers: () => void;
  scrollToTime: (timestamp: number) => void;
  setPointMarker: (time: number, price: number) => void;  // AI Agent 用来显示选中的点
}

const MultiPaneChart = forwardRef<MultiPaneChartRef, MultiPaneChartProps>(({
  ohlcData,
  interval,
  width = 0,
  mainHeight = 400,
  subHeight = 180,
  onRectChange,
  onPointsChange,
  lastUpdateType,
  lastUpdatedCandle,
  hideToolbar = false,
  showVolume = false,
  showRsi = false,
  showMacd = false,
  showSma = false,
  showEma = false,
  showWma = false,
  showWema = false,
  showTypicalPrice = false,
  showObv = false,
  showBollingerBands = false,
  showWilliamsR = false,
  showAtr = false,
  showMfi = false,
  showStochastic = false,
  showRoc = false,
  showTrix = false,
  showVwap = false,
  showAdl = false,
  showAdx = false,
  showAo = false,
  showCci = false,
  showForceIndex = false,
  showKst = false,
  showPsar = false,
  showStochRsi = false,
  showVolumeProfile = false,
  showIchimokuCloud = false,
  indicatorPlacements = {},
  onIndicatorToggle,
  onPlacementChange,
}, ref) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const mainChartRef = useRef<HTMLDivElement>(null);
  const macdChartRef = useRef<HTMLDivElement>(null);
  const volumeChartRef = useRef<HTMLDivElement>(null);
  const obvChartRef = useRef<HTMLDivElement>(null);
  const williamsChartRef = useRef<HTMLDivElement>(null);
  const atrChartRef = useRef<HTMLDivElement>(null);
  const mfiChartRef = useRef<HTMLDivElement>(null);
  const stochasticChartRef = useRef<HTMLDivElement>(null);
  const rocChartRef = useRef<HTMLDivElement>(null);
  const trixChartRef = useRef<HTMLDivElement>(null);
  const adlChartRef = useRef<HTMLDivElement>(null);
  const adxChartRef = useRef<HTMLDivElement>(null);
  const aoChartRef = useRef<HTMLDivElement>(null);
  const cciChartRef = useRef<HTMLDivElement>(null);
  const fiChartRef = useRef<HTMLDivElement>(null);
  const kstChartRef = useRef<HTMLDivElement>(null);
  const stochRsiChartRef = useRef<HTMLDivElement>(null);
  const overlayPaneRefs = useRef<Partial<Record<IndicatorId, HTMLDivElement | null>>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const rectangleToolRef = useRef<RectangleToolRef>(null);
  const pointToolRef = useRef<PointToolRef>(null);

  const mainChart = useRef<IChartApi | null>(null);
  const macdChart = useRef<IChartApi | null>(null);
  const volumeChart = useRef<IChartApi | null>(null);
  const obvChart = useRef<IChartApi | null>(null);
  const williamsChart = useRef<IChartApi | null>(null);
  const atrChart = useRef<IChartApi | null>(null);
  const mfiChart = useRef<IChartApi | null>(null);
  const stochasticChart = useRef<IChartApi | null>(null);
  const rocChart = useRef<IChartApi | null>(null);
  const trixChart = useRef<IChartApi | null>(null);
  const adlChart = useRef<IChartApi | null>(null);
  const adxChart = useRef<IChartApi | null>(null);
  const aoChart = useRef<IChartApi | null>(null);
  const cciChart = useRef<IChartApi | null>(null);
  const fiChart = useRef<IChartApi | null>(null);
  const kstChart = useRef<IChartApi | null>(null);
  const stochRsiChart = useRef<IChartApi | null>(null);
  const overlayPaneCharts = useRef<Partial<Record<IndicatorId, IChartApi | null>>>({});
  const overlayPaneCrosshairHandlers = useRef<Partial<Record<IndicatorId, (param: any) => void>>>({});
  const overlayLegendRef = useRef<HTMLDivElement | null>(null);

  const candleSeriesRef = useRef<any>(null);
  const areaSeriesRef = useRef<any>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const macdHistogramSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const macdLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdSignalSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const obvSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const williamsSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const atrSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const mfiSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const stochasticKSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const stochasticDSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const rocSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const trixSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const vwapSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adlSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adxSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adxPlusDiSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const adxMinusDiSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const aoSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const cciSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const fiSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const kstSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const kstSignalSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const stochRsiKSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const stochRsiDSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const overlaySeriesRef = useRef<Record<string, { series: ISeriesApi<'Line'>; target: 'main' | 'overlayPane'; baseId: IndicatorId }>>({});
  const volumeSyncGuardRef = useRef(false);
  const macdSyncGuardRef = useRef(false);
  const obvSyncGuardRef = useRef(false);
  const williamsSyncGuardRef = useRef(false);
  const atrSyncGuardRef = useRef(false);
  const mfiSyncGuardRef = useRef(false);
  const stochasticSyncGuardRef = useRef(false);
  const rocSyncGuardRef = useRef(false);
  const trixSyncGuardRef = useRef(false);
  const vwapSyncGuardRef = useRef(false);
  const adlSyncGuardRef = useRef(false);
  const adxSyncGuardRef = useRef(false);
  const aoSyncGuardRef = useRef(false);
  const cciSyncGuardRef = useRef(false);
  const fiSyncGuardRef = useRef(false);
  const kstSyncGuardRef = useRef(false);
  const stochRsiSyncGuardRef = useRef(false);
  const overlayPaneSyncGuardRef = useRef(false);
  const overlayPaneTimeSyncGuardRef = useRef(false);
  const overlayPaneLogicalSyncGuardRef = useRef(false);
  const indicatorPaneTimeSyncGuardRef = useRef(false);
  const userDetachedFromRightRef = useRef(false);
  const priceScaleWidthRef = useRef<number>(0);
  const isInitialDataSet = useRef<boolean>(false);

  const [activeTool, setActiveTool] = React.useState<'rectangle' | 'point' | null>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(800);
  const [rectToolActive, setRectToolActive] = React.useState(false);
  const [pointToolActive, setPointToolActive] = React.useState(false);
  const [hasSelectedRect, setHasSelectedRect] = React.useState(false);
  const [hasSelectedPoints, setHasSelectedPoints] = React.useState(false);
  const [volumeProfileVersion, setVolumeProfileVersion] = React.useState(0);
  const [volumeProfileBuckets, setVolumeProfileBuckets] = React.useState(30);
  const [volumeProfileWidthPct, setVolumeProfileWidthPct] = React.useState(0.25);
  const [volumeProfileValueAreaPct, setVolumeProfileValueAreaPct] = React.useState(0.7);
  const [tooltip, setTooltip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    time: string;
    price: string;
    ohlc: { open: number; high: number; low: number; close: number };
  }>({
    visible: false,
    x: 0,
    y: 0,
    time: '',
    price: '',
    ohlc: { open: 0, high: 0, low: 0, close: 0 }
  });
  const [overlayPaneVersion, setOverlayPaneVersion] = useState(0);
  const [sharedCrosshairLogical, setSharedCrosshairLogical] = useState<number | null>(null);
  const [sharedCrosshairTime, setSharedCrosshairTime] = useState<number | null>(null);
  const [sharedCrosshairPrice, setSharedCrosshairPrice] = useState<number | null>(null);

  // 短选区提示相关状态
  const SHORT_SELECTION_HINT_KEY = 'hideShortSelectionHint';
  const [showShortSelectionHint, setShowShortSelectionHint] = useState(false);
  const [shortSelectionCount, setShortSelectionCount] = useState(0);

  const handleRectToolToggle = () => {
    const newActive = !rectToolActive;
    setRectToolActive(newActive);
    setPointToolActive(false);
    setActiveTool(newActive ? 'rectangle' : null);

    if (mainChart.current) {
      mainChart.current.applyOptions({
        handleScroll: !newActive,
        handleScale: !newActive,
      });
    }
  };

  const handlePointToolToggle = () => {
    const newActive = !pointToolActive;
    setPointToolActive(newActive);
    setRectToolActive(false);
    setActiveTool(newActive ? 'point' : null);

    if (mainChart.current) {
      mainChart.current.applyOptions({
        handleScroll: !newActive,
        handleScale: !newActive,
      });
    }
  };

  const handleClearRect = () => {
    setHasSelectedRect(false);
    if (rectangleToolRef.current) {
      rectangleToolRef.current.clearArea();
    }
    if (onRectChange) onRectChange(null);
  };

  const handleClearPoints = () => {
    setHasSelectedPoints(false);
    if (pointToolRef.current) {
      pointToolRef.current.clearPoints();
    }
    if (onPointsChange) onPointsChange([]);
  };

  const handleRectSelectModeChange = (selecting: boolean) => {
    setRectToolActive(selecting);
    setActiveTool(selecting ? 'rectangle' : null);
  };

  const handlePointSelectModeChange = (selecting: boolean) => {
    setPointToolActive(selecting);
    setActiveTool(selecting ? 'point' : null);
  };

  const handleRectChange = (rect: any) => {
    setHasSelectedRect(!!rect);
    
    // 检测选中长度，如果 <= 5 则显示提示
    if (rect) {
      const selectedCount = rect.endIndex - rect.startIndex + 1;
      const shouldHideHint = localStorage.getItem(SHORT_SELECTION_HINT_KEY) === 'true';
      if (selectedCount <= 5 && !shouldHideHint) {
        setShortSelectionCount(selectedCount);
        setShowShortSelectionHint(true);
      } else {
        setShowShortSelectionHint(false);
      }
    } else {
      setShowShortSelectionHint(false);
    }
    
    if (onRectChange) onRectChange(rect);
  };

  const handlePointsChange = (points: { time: number; price: number }[]) => {
    setHasSelectedPoints(points.length > 0);
    if (onPointsChange) onPointsChange(points);
  };

  const getActualWidth = () => {
    if (width > 0) return width;
    return containerWidth;
  };
  const volumeProfileWidth = Math.max(60, Math.round(getActualWidth() * volumeProfileWidthPct));

  const clampNumber = (value: number | null | undefined, min: number, max: number, fallback: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
    return Math.min(max, Math.max(min, value));
  };

  const shouldAutoScrollToRealTime = () => {
    if (userDetachedFromRightRef.current) return false;
    if (!mainChart.current || !candleSeriesRef.current) return true;
    const range = mainChart.current.timeScale().getVisibleLogicalRange();
    if (!range) return true;
    const barsInfo = candleSeriesRef.current.barsInLogicalRange(range);
    if (!barsInfo || typeof barsInfo.barsAfter !== 'number') return true;
    return barsInfo.barsAfter <= 0;
  };

  const scrollToRealTimeIfLive = (chart: IChartApi | null) => {
    if (!chart) return;
    if (!shouldAutoScrollToRealTime()) return;
    chart.timeScale().scrollToRealTime();
  };

  const sharedTickFormatter = useCallback((time: any) => {
    const ts = typeof time === 'number' ? time : (time?.time ?? time);
    if (!ts || isNaN(ts)) return '';
    const d = new Date(ts * 1000);
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const i = (window as any)?.selectedTimeframe as string | undefined;
    if (i && i.toLowerCase() === '1m') return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    if (i && i.toLowerCase() === '1h')
      return `${d.toLocaleString(undefined, { month: 'short' })} ${d.getDate()} ${pad(d.getHours())}:00`;
    return `${d.toLocaleString(undefined, { month: 'short' })} ${d.getDate()}`;
  }, []);

  const applyTimeScaleAppearance = useCallback(
    (chart: IChartApi | null | undefined, showAxis: boolean) => {
      if (!chart || !(chart as any).timeScale) return;
      try {
        chart.applyOptions({
          timeScale: {
            barSpacing: 12,
            minBarSpacing: 8,
            timeVisible: true,
            borderColor: showAxis ? theme.chartGrid : 'transparent',
            tickMarkFormatter: showAxis ? sharedTickFormatter : () => '',
          },
        });
      } catch {
        // ignore disposed chart
      }
    },
    [sharedTickFormatter, theme.chartGrid]
  );

  const isChartAlive = (chart: any): chart is IChartApi => {
    if (!chart || typeof chart.timeScale !== 'function' || typeof chart.applyOptions !== 'function') return false;
    try {
      chart.timeScale();
      return true;
    } catch {
      return false;
    }
  };

  const isTimeScaleAlive = (scale: any): scale is ReturnType<IChartApi['timeScale']> => {
    if (!scale || typeof scale.getVisibleLogicalRange !== 'function') return false;
    try {
      scale.getVisibleLogicalRange();
      return true;
    } catch {
      return false;
    }
  };

  const getSafeTimeScale = (chart: IChartApi | null | undefined) => {
    if (!chart) return null;
    try {
      return chart.timeScale();
    } catch {
      return null;
    }
  };

  const safeRemoveChart = (
    chartRef: React.MutableRefObject<IChartApi | null>,
    seriesRefs: Array<React.MutableRefObject<any>>
  ) => {
    const chart = chartRef.current;
    chartRef.current = null;
    seriesRefs.forEach((ref) => {
      if (ref) ref.current = null;
    });
    try {
      chart?.remove();
    } catch {
      // ignore if already disposed
    }
  };

  const cleanOhlc = useMemo(
    () =>
      (ohlcData || []).filter(
        (item) =>
          Number.isFinite(item?.time) &&
          Number.isFinite(item?.open) &&
          Number.isFinite(item?.high) &&
          Number.isFinite(item?.low) &&
          Number.isFinite(item?.close)
      ),
    [ohlcData]
  );

  const ohlcByTime = useMemo(() => {
    const map = new Map<number, any>();
    cleanOhlc.forEach((item) => {
      const t = Number(item.time);
      if (Number.isFinite(t)) {
        map.set(t, item);
      }
    });
    return map;
  }, [cleanOhlc]);

  const closeValues = useMemo(() => cleanOhlc.map((item) => Number(item?.close ?? 0)), [cleanOhlc]);
  const highValues = useMemo(() => cleanOhlc.map((item) => Number(item?.high ?? 0)), [cleanOhlc]);
  const lowValues = useMemo(() => cleanOhlc.map((item) => Number(item?.low ?? 0)), [cleanOhlc]);
  const volumes = useMemo(() => cleanOhlc.map((item) => Number(item?.volume ?? 0)), [cleanOhlc]);
  const times = useMemo(
    () => cleanOhlc.map((item) => Number(item?.time ?? 0) as UTCTimestamp),
    [cleanOhlc]
  );
  const inferredIntervalSec = useMemo(() => {
    if (times.length < 2) return 0;
    const diffs: number[] = [];
    for (let i = 1; i < times.length; i++) {
      const diff = Number(times[i]) - Number(times[i - 1]);
      if (Number.isFinite(diff) && diff > 0) {
        diffs.push(diff);
      }
    }
    if (!diffs.length) return 0;
    diffs.sort((a, b) => a - b);
    return Math.round(diffs[Math.floor(diffs.length / 2)]);
  }, [times]);

  const intervalSec = useMemo(() => {
    const map: Record<string, number> = {
      '1m': 60,
      '5m': 300,
      '15m': 900,
      '30m': 1800,
      '1h': 3600,
      '4h': 14400,
      '1d': 86400,
    };
    const key = (interval || '').toLowerCase();
    return map[key] ?? (inferredIntervalSec > 0 ? inferredIntervalSec : 0);
  }, [interval, inferredIntervalSec]);

type RsiSeriesPoint = LineData<UTCTimestamp> | WhitespaceData<UTCTimestamp>;
interface MacdSeriesPoint {
  time: UTCTimestamp;
  macd?: number;
  signal?: number;
  histogram?: number;
}

interface StochasticPoint {
  time: UTCTimestamp;
  k?: number;
  d?: number;
}

interface StochRsiPoint {
  time: UTCTimestamp;
  k?: number;
  d?: number;
}

interface TrixPoint {
  time: UTCTimestamp;
  value?: number;
}

interface VwapPoint {
  time: UTCTimestamp;
  value?: number;
}

interface AdlPoint {
  time: UTCTimestamp;
  value?: number;
}

interface AdxPoint {
  time: UTCTimestamp;
  adx?: number;
  pdi?: number;
  mdi?: number;
}

interface AoPoint {
  time: UTCTimestamp;
  value?: number;
}

interface CciPoint {
  time: UTCTimestamp;
  value?: number;
}

interface FiPoint {
  time: UTCTimestamp;
  value?: number;
}

interface KstPoint {
  time: UTCTimestamp;
  kst?: number;
  signal?: number;
}

interface PsarPoint {
  time: UTCTimestamp;
  value?: number;
}

interface VolumeProfilePoint {
  rangeStart: number;
  rangeEnd: number;
  bullishVolume: number;
  bearishVolume: number;
  totalVolume?: number;
}

interface IchimokuPoint {
  time: UTCTimestamp;
  conversion?: number;
  base?: number;
  spanA?: number;
  spanB?: number;
}

  const rsiData = useMemo(() => {
    if (!showRsi || !ohlcData || ohlcData.length === 0) {
      return [];
    }

    const closes = ohlcData.map((item) => Number(item?.close ?? 0));
    const period = 14;

    try {
      const rsiValues = TechnicalRSI.calculate({ values: closes, period });
      const startIndex = closes.length - rsiValues.length;

      const result: RsiSeriesPoint[] = ohlcData.map((item, idx) => {
        const time = Number(item.time ?? 0) as UTCTimestamp;
        if (idx >= startIndex) {
          const rsiValue = Number(rsiValues[idx - startIndex] ?? 0);
          return { time, value: rsiValue };
        }
        return { time };
      });

      return result;
    } catch (error) {
      logger.error('Failed to calculate RSI', error);
      return [];
    }
  }, [ohlcData, showRsi]);

  const macdData = useMemo(() => {
    if (!showMacd || !ohlcData || ohlcData.length === 0) {
      return [];
    }

    const closes = ohlcData.map((item) => Number(item?.close ?? 0));
    try {
      const macdValues = TechnicalMACD.calculate({
        values: closes,
        fastPeriod: 12,
        slowPeriod: 26,
        signalPeriod: 9,
        SimpleMAOscillator: false,
        SimpleMASignal: false,
      });
      const startIndex = closes.length - macdValues.length;

      const result: MacdSeriesPoint[] = ohlcData.map((item, idx) => {
        const time = Number(item.time ?? 0) as UTCTimestamp;
        if (idx >= startIndex) {
          const macdPoint = macdValues[idx - startIndex] || {};
          return {
            time,
            macd: typeof macdPoint.MACD === 'number' ? macdPoint.MACD : undefined,
            signal: typeof macdPoint.signal === 'number' ? macdPoint.signal : undefined,
            histogram: typeof macdPoint.histogram === 'number' ? macdPoint.histogram : undefined,
          };
        }
        return { time };
      });

      return result;
    } catch (error) {
      logger.error('Failed to calculate MACD', error);
      return [];
    }
  }, [ohlcData, showMacd]);

  const latestMacd = useMemo(() => {
    for (let i = macdData.length - 1; i >= 0; i--) {
      const point = macdData[i];
      if (point.macd !== undefined) {
        return {
          macd: point.macd,
          signal: point.signal,
        };
      }
    }
    return undefined;
  }, [macdData]);

  const smaData = useMemo(() => {
    if (!showSma || closeValues.length === 0) return [];
    const period = 20;
    const vals = TechnicalSMA.calculate({ period, values: closeValues });
    const offset = closeValues.length - vals.length;
    return times.map((time, idx) => {
      if (idx >= offset) {
        const v = vals[idx - offset];
        const value = Number(v);
        if (Number.isFinite(value)) {
          return { time, value };
        }
      }
      return { time };
    });
  }, [showSma, closeValues, times]);

  const emaData = useMemo(() => {
    if (!showEma || closeValues.length === 0) return [];
    const period = 20;
    const vals = TechnicalEMA.calculate({ period, values: closeValues });
    const offset = closeValues.length - vals.length;
    return times.map((time, idx) => {
      if (idx >= offset) {
        const v = vals[idx - offset];
        const value = Number(v);
        if (Number.isFinite(value)) {
          return { time, value };
        }
      }
      return { time };
    });
  }, [showEma, closeValues, times]);

  const wmaData = useMemo(() => {
    if (!showWma || closeValues.length === 0) return [];
    const period = 20;
    const vals = TechnicalWMA.calculate({ period, values: closeValues });
    const offset = closeValues.length - vals.length;
    return times.map((time, idx) => {
      if (idx >= offset) {
        const v = vals[idx - offset];
        const value = Number(v);
        if (Number.isFinite(value)) {
          return { time, value };
        }
      }
      return { time };
    });
  }, [showWma, closeValues, times]);

  const wemaData = useMemo(() => {
    if (!showWema || closeValues.length === 0) return [];
    const period = 20;
    const vals = TechnicalWEMA.calculate({ period, values: closeValues });
    const offset = closeValues.length - vals.length;
    return vals
      .map((v, idx) => {
        const t = times[offset + idx];
        return { time: t, value: v };
      })
      .filter((d) => Number.isFinite(d.time) && Number.isFinite(d.value));
  }, [showWema, closeValues, times]);

  const typicalPriceData = useMemo(() => {
    if (!showTypicalPrice || cleanOhlc.length === 0) return [];
    return cleanOhlc
      .map((item) => ({
        time: Number(item.time ?? 0) as UTCTimestamp,
        value: (Number(item.high ?? 0) + Number(item.low ?? 0) + Number(item.close ?? 0)) / 3,
      }))
      .filter((d) => Number.isFinite(d.time) && Number.isFinite(d.value));
  }, [showTypicalPrice, cleanOhlc]);

  const bollingerData = useMemo(() => {
    if (!showBollingerBands || closeValues.length === 0) {
      return { upper: [] as any[], middle: [] as any[], lower: [] as any[] };
    }

    const period = 20;
    const stdDev = 2;
    try {
      const vals = TechnicalBollingerBands.calculate({ period, stdDev, values: closeValues });
      const offset = closeValues.length - vals.length;
      const upper = times.map((time, idx) => {
        if (idx >= offset) {
          const v = vals[idx - offset];
          const value = Number(v?.upper);
          if (Number.isFinite(value)) return { time, value };
        }
        return { time };
      });
      const middle = times.map((time, idx) => {
        if (idx >= offset) {
          const v = vals[idx - offset];
          const value = Number(v?.middle);
          if (Number.isFinite(value)) return { time, value };
        }
        return { time };
      });
      const lower = times.map((time, idx) => {
        if (idx >= offset) {
          const v = vals[idx - offset];
          const value = Number(v?.lower);
          if (Number.isFinite(value)) return { time, value };
        }
        return { time };
      });
      return { upper, middle, lower };
    } catch (error) {
      logger.error('Failed to calculate Bollinger Bands', error);
      return { upper: [], middle: [], lower: [] };
    }
  }, [showBollingerBands, closeValues, times]);

  const psarData = useMemo<PsarPoint[]>(() => {
    if (!showPsar || cleanOhlc.length === 0) return [];
    try {
      const values = TechnicalPSAR.calculate({
        high: highValues,
        low: lowValues,
        step: 0.02,
        max: 0.2,
      });
      const offset = highValues.length - values.length;
      return values
        .map((v, idx) => ({
          time: times[offset + idx],
          value: Number(v),
        }))
        .filter((d) => Number.isFinite(d.time) && Number.isFinite(d.value));
    } catch (error) {
      logger.error('Failed to calculate PSAR', error);
      return [];
    }
  }, [showPsar, cleanOhlc, highValues, lowValues, times]);

  const ichimokuData = useMemo<IchimokuPoint[]>(() => {
    if (!showIchimokuCloud || cleanOhlc.length === 0) return [];
    const conversionPeriod = 9;
    const basePeriod = 26;
    const spanPeriod = 52;
    const displacement = 26;
    try {
      const values = TechnicalIchimokuCloud.calculate({
        high: highValues,
        low: lowValues,
        conversionPeriod,
        basePeriod,
        spanPeriod,
        displacement,
      });
      const offset = highValues.length - values.length;
      const conversionMap = new Map<number, number>();
      const baseMap = new Map<number, number>();
      const spanAMap = new Map<number, number>();
      const spanBMap = new Map<number, number>();

      values.forEach((value, idx) => {
        const baseIndex = offset + idx;
        if (baseIndex < 0 || baseIndex >= times.length) return;
        if (typeof value.conversion === 'number') {
          conversionMap.set(baseIndex, Number(value.conversion));
        }
        if (typeof value.base === 'number') {
          baseMap.set(baseIndex, Number(value.base));
        }
        const spanIndex = baseIndex + displacement;
        if (spanIndex >= 0 && spanIndex < times.length) {
          if (typeof value.spanA === 'number') {
            spanAMap.set(spanIndex, Number(value.spanA));
          }
          if (typeof value.spanB === 'number') {
            spanBMap.set(spanIndex, Number(value.spanB));
          }
        }
      });

      return times.map((time, idx) => ({
        time,
        conversion: conversionMap.get(idx),
        base: baseMap.get(idx),
        spanA: spanAMap.get(idx),
        spanB: spanBMap.get(idx),
      }));
    } catch (error) {
      logger.error('Failed to calculate Ichimoku Cloud', error);
      return [];
    }
  }, [showIchimokuCloud, cleanOhlc, highValues, lowValues, times]);

  const ichimokuConversionData = useMemo(
    () =>
      showIchimokuCloud
        ? ichimokuData.map((point) =>
            typeof point.conversion === 'number'
              ? { time: point.time, value: point.conversion }
              : { time: point.time }
          )
        : [],
    [showIchimokuCloud, ichimokuData]
  );

  const ichimokuBaseData = useMemo(
    () =>
      showIchimokuCloud
        ? ichimokuData.map((point) =>
            typeof point.base === 'number' ? { time: point.time, value: point.base } : { time: point.time }
          )
        : [],
    [showIchimokuCloud, ichimokuData]
  );

  const ichimokuSpanAData = useMemo(
    () =>
      showIchimokuCloud
        ? ichimokuData.map((point) =>
            typeof point.spanA === 'number' ? { time: point.time, value: point.spanA } : { time: point.time }
          )
        : [],
    [showIchimokuCloud, ichimokuData]
  );

  const ichimokuSpanBData = useMemo(
    () =>
      showIchimokuCloud
        ? ichimokuData.map((point) =>
            typeof point.spanB === 'number' ? { time: point.time, value: point.spanB } : { time: point.time }
          )
        : [],
    [showIchimokuCloud, ichimokuData]
  );

  const volumeProfileData = useMemo<VolumeProfilePoint[]>(() => {
    if (!showVolumeProfile || cleanOhlc.length === 0) return [];
    try {
      const range = mainChart.current?.timeScale().getVisibleLogicalRange();
      const total = cleanOhlc.length;
      const fromIndex = Math.max(0, Math.floor(range?.from ?? 0));
      const toIndex = Math.min(total - 1, Math.ceil(range?.to ?? total - 1));
      const slice = cleanOhlc.slice(fromIndex, toIndex + 1);
      if (slice.length === 0) return [];
      const sliceHigh = slice.map((item) => Number(item?.high ?? 0));
      const sliceLow = slice.map((item) => Number(item?.low ?? 0));
      const sliceOpen = slice.map((item) => Number(item?.open ?? 0));
      const sliceClose = slice.map((item) => Number(item?.close ?? 0));
      const sliceVolume = slice.map((item) => Number(item?.volume ?? 0));

      const values = TechnicalVolumeProfile.calculate({
        high: sliceHigh,
        low: sliceLow,
        open: sliceOpen,
        close: sliceClose,
        volume: sliceVolume,
        noOfBars: volumeProfileBuckets,
      }) as unknown as VolumeProfilePoint[];

      return values.filter(
        (bucket) =>
          Number.isFinite(bucket.rangeStart) &&
          Number.isFinite(bucket.rangeEnd) &&
          (Number.isFinite(bucket.bullishVolume) || Number.isFinite(bucket.bearishVolume))
      );
    } catch (error) {
      logger.error('Failed to calculate Volume Profile', error);
      return [];
    }
  }, [showVolumeProfile, cleanOhlc, volumeProfileBuckets, volumeProfileVersion]);

  const volumeProfileMax = useMemo(() => {
    if (!volumeProfileData.length) return 0;
    return volumeProfileData.reduce((max, bucket) => {
      const total =
        Number(bucket.totalVolume) ||
        (Number(bucket.bullishVolume) || 0) + (Number(bucket.bearishVolume) || 0);
      return Math.max(max, total);
    }, 0);
  }, [volumeProfileData]);

  const volumeProfileStats = useMemo(() => {
    if (!volumeProfileData.length) {
      return { pocIndex: -1, valueAreaSet: new Set<number>(), vah: undefined, val: undefined };
    }
    const totals = volumeProfileData.map((bucket) => {
      const total =
        Number(bucket.totalVolume) ||
        (Number(bucket.bullishVolume) || 0) + (Number(bucket.bearishVolume) || 0);
      return total;
    });
    const totalVolume = totals.reduce((sum, v) => sum + v, 0);
    if (totalVolume <= 0) {
      return { pocIndex: -1, valueAreaSet: new Set<number>(), vah: undefined, val: undefined };
    }
    let pocIndex = 0;
    totals.forEach((value, idx) => {
      if (value > totals[pocIndex]) pocIndex = idx;
    });

    const target = totalVolume * volumeProfileValueAreaPct;
    const sorted = totals
      .map((value, idx) => ({ value, idx }))
      .sort((a, b) => b.value - a.value);
    const valueAreaSet = new Set<number>();
    let acc = 0;
    let val = Infinity;
    let vah = -Infinity;
    for (const item of sorted) {
      valueAreaSet.add(item.idx);
      acc += item.value;
      const bucket = volumeProfileData[item.idx];
      if (bucket) {
        val = Math.min(val, bucket.rangeStart);
        vah = Math.max(vah, bucket.rangeEnd);
      }
      if (acc >= target) break;
    }
    return {
      pocIndex,
      valueAreaSet,
      vah: Number.isFinite(vah) ? vah : undefined,
      val: Number.isFinite(val) ? val : undefined,
    };
  }, [volumeProfileData, volumeProfileValueAreaPct]);

  const volumeProfileBars = useMemo(() => {
    if (!showVolumeProfile || !volumeProfileData.length || !candleSeriesRef.current) return [];
    const maxVolume = volumeProfileMax || 1;
    return volumeProfileData
      .map((bucket, idx) => {
        const yStart = candleSeriesRef.current?.priceToCoordinate(bucket.rangeStart);
        const yEnd = candleSeriesRef.current?.priceToCoordinate(bucket.rangeEnd);
        if (yStart === null || yStart === undefined || yEnd === null || yEnd === undefined) {
          return null;
        }
        const top = Math.min(yStart, yEnd);
        const height = Math.max(1, Math.abs(yEnd - yStart));
        const total =
          Number(bucket.totalVolume) ||
          (Number(bucket.bullishVolume) || 0) + (Number(bucket.bearishVolume) || 0);
        const width = Math.max(2, (total / maxVolume) * volumeProfileWidth);
        return {
          top,
          height,
          width,
          isPoc: idx === volumeProfileStats.pocIndex,
          isValueArea: volumeProfileStats.valueAreaSet.has(idx),
        };
      })
      .filter(Boolean) as Array<{
      top: number;
      height: number;
      width: number;
      isPoc: boolean;
      isValueArea: boolean;
    }>;
  }, [
    showVolumeProfile,
    volumeProfileData,
    volumeProfileMax,
    volumeProfileVersion,
    volumeProfileWidth,
    containerWidth,
    mainHeight,
    volumeProfileStats,
  ]);

  const volumeProfileLineCoords = useMemo(() => {
    if (!showVolumeProfile || !candleSeriesRef.current) {
      return { vahY: undefined, valY: undefined };
    }
    const vahY =
      typeof volumeProfileStats.vah === 'number'
        ? candleSeriesRef.current.priceToCoordinate(volumeProfileStats.vah)
        : null;
    const valY =
      typeof volumeProfileStats.val === 'number'
        ? candleSeriesRef.current.priceToCoordinate(volumeProfileStats.val)
        : null;
    return {
      vahY: vahY ?? undefined,
      valY: valY ?? undefined,
    };
  }, [showVolumeProfile, volumeProfileStats, volumeProfileVersion, containerWidth, mainHeight]);

  const ichimokuCloudSegments = useMemo(() => {
    if (!showIchimokuCloud || !mainChart.current || !candleSeriesRef.current) return [];
    const timeScale = mainChart.current.timeScale();
    const segments: Array<{ path: string; bullish: boolean }> = [];
    for (let i = 1; i < ichimokuData.length; i++) {
      const prev = ichimokuData[i - 1];
      const curr = ichimokuData[i];
      if (
        typeof prev.spanA !== 'number' ||
        typeof prev.spanB !== 'number' ||
        typeof curr.spanA !== 'number' ||
        typeof curr.spanB !== 'number'
      ) {
        continue;
      }
      const x1 = timeScale.timeToCoordinate(prev.time);
      const x2 = timeScale.timeToCoordinate(curr.time);
      if (x1 === null || x2 === null) continue;
      const yA1 = candleSeriesRef.current.priceToCoordinate(prev.spanA);
      const yA2 = candleSeriesRef.current.priceToCoordinate(curr.spanA);
      const yB1 = candleSeriesRef.current.priceToCoordinate(prev.spanB);
      const yB2 = candleSeriesRef.current.priceToCoordinate(curr.spanB);
      if (
        yA1 === null ||
        yA2 === null ||
        yB1 === null ||
        yB2 === null
      ) {
        continue;
      }
      const bullish = (prev.spanA + curr.spanA) / 2 >= (prev.spanB + curr.spanB) / 2;
      const path = `M ${x1} ${yA1} L ${x2} ${yA2} L ${x2} ${yB2} L ${x1} ${yB1} Z`;
      segments.push({ path, bullish });
    }
    return segments;
  }, [
    showIchimokuCloud,
    ichimokuData,
    volumeProfileVersion,
    containerWidth,
    mainHeight,
  ]);

  const obvData = useMemo(() => {
    if (!showObv || closeValues.length === 0) return [];
    const vals = TechnicalOBV.calculate({ close: closeValues, volume: volumes });
    const offset = closeValues.length - vals.length;
    return vals
      .map((v, idx) => ({ time: times[offset + idx], value: v }))
      .filter((d) => Number.isFinite(d.time) && Number.isFinite(d.value));
  }, [showObv, closeValues, volumes, times]);

  const williamsData = useMemo(() => {
    if (!showWilliamsR || cleanOhlc.length === 0) return [];
    const period = 14;
    try {
      const values = TechnicalWilliamsR.calculate({
        high: highValues,
        low: lowValues,
        close: closeValues,
        period,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = Number(values[idx - offset]);
          if (Number.isFinite(v)) {
            return { time, value: v };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate Williams %R', error);
      return [];
    }
  }, [showWilliamsR, cleanOhlc, closeValues, highValues, lowValues, times]);

  const stochasticData = useMemo<StochasticPoint[]>(() => {
    if (!showStochastic || cleanOhlc.length === 0) return [];
    const period = 14;
    const signalPeriod = 3;
    try {
      const values = TechnicalStochastic.calculate({
        high: highValues,
        low: lowValues,
        close: closeValues,
        period,
        signalPeriod,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const k = typeof v?.k === 'number' ? Number(v.k) : undefined;
          const d = typeof v?.d === 'number' ? Number(v.d) : undefined;
          if (Number.isFinite(k) || Number.isFinite(d)) {
            return { time, k, d };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate Stochastic', error);
      return [];
    }
  }, [showStochastic, cleanOhlc, highValues, lowValues, closeValues, times]);

  const stochRsiData = useMemo<StochRsiPoint[]>(() => {
    if (!showStochRsi || cleanOhlc.length === 0) return [];
    const rsiPeriod = 14;
    const stochasticPeriod = 14;
    const kPeriod = 3;
    const dPeriod = 3;
    try {
      const values = TechnicalStochasticRSI.calculate({
        values: closeValues,
        rsiPeriod,
        stochasticPeriod,
        kPeriod,
        dPeriod,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const k = typeof v?.k === 'number' ? Number(v.k) : undefined;
          const d = typeof v?.d === 'number' ? Number(v.d) : undefined;
          if (k !== undefined || d !== undefined) {
            return { time, k, d };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate StochRSI', error);
      return [];
    }
  }, [showStochRsi, cleanOhlc, closeValues, times]);

  const rocData = useMemo(() => {
    if (!showRoc || cleanOhlc.length === 0) return [];
    const period = 14;
    try {
      const values = TechnicalROC.calculate({
        values: closeValues,
        period,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const value = Number(v);
          if (Number.isFinite(value)) {
            return { time, value };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate ROC', error);
      return [];
    }
  }, [showRoc, cleanOhlc, closeValues, times]);

  const atrData = useMemo(() => {
    if (!showAtr || cleanOhlc.length === 0) return [];
    const period = 14;
    try {
      const values = TechnicalATR.calculate({
        high: highValues,
        low: lowValues,
        close: closeValues,
        period,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const value = Number(v);
          if (Number.isFinite(value)) {
            return { time, value };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate ATR', error);
      return [];
    }
  }, [showAtr, cleanOhlc, closeValues, highValues, lowValues, times]);

  const mfiData = useMemo(() => {
    if (!showMfi || cleanOhlc.length === 0) return [];
    const period = 14;
    try {
      const values = TechnicalMFI.calculate({
        high: highValues,
        low: lowValues,
        close: closeValues,
        volume: volumes,
        period,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const value = Number(v);
          if (Number.isFinite(value)) {
            return { time, value };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate MFI', error);
      return [];
    }
  }, [showMfi, cleanOhlc, closeValues, highValues, lowValues, volumes, times]);

  const macdByTime = useMemo(() => {
    const map = new Map<number, number>();
    macdData.forEach((point) => {
      if (point.macd !== undefined) {
        map.set(Number(point.time), point.macd);
      }
    });
    return map;
  }, [macdData]);

  const williamsByTime = useMemo(() => {
    const map = new Map<number, number>();
    williamsData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [williamsData]);

  const atrByTime = useMemo(() => {
    const map = new Map<number, number>();
    atrData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [atrData]);

  const mfiByTime = useMemo(() => {
    const map = new Map<number, number>();
    mfiData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [mfiData]);

  const stochasticKByTime = useMemo(() => {
    const map = new Map<number, number>();
    stochasticData.forEach((item) => {
      if (item && typeof item.k === 'number') {
        map.set(Number(item.time), item.k);
      }
    });
    return map;
  }, [stochasticData]);

  const stochRsiKByTime = useMemo(() => {
    const map = new Map<number, number>();
    stochRsiData.forEach((item) => {
      if (item && typeof item.k === 'number') {
        map.set(Number(item.time), item.k);
      }
    });
    return map;
  }, [stochRsiData]);

  const rocByTime = useMemo(() => {
    const map = new Map<number, number>();
    rocData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [rocData]);

  const latestWilliams = useMemo(() => {
    if (!williamsData.length) return undefined;
    const last = williamsData[williamsData.length - 1];
    return typeof last.value === 'number' ? last.value : undefined;
  }, [williamsData]);

  const latestAtr = useMemo(() => {
    if (!atrData.length) return undefined;
    const last = atrData[atrData.length - 1];
    return typeof last.value === 'number' ? last.value : undefined;
  }, [atrData]);

  const latestMfi = useMemo(() => {
    if (!mfiData.length) return undefined;
    const last = mfiData[mfiData.length - 1];
    return typeof last.value === 'number' ? last.value : undefined;
  }, [mfiData]);

  const latestStochastic = useMemo(() => {
    for (let i = stochasticData.length - 1; i >= 0; i--) {
      const point = stochasticData[i];
      if (typeof point.k === 'number' || typeof point.d === 'number') {
        return point;
      }
    }
    return undefined;
  }, [stochasticData]);

  const latestStochRsi = useMemo(() => {
    for (let i = stochRsiData.length - 1; i >= 0; i--) {
      const point = stochRsiData[i];
      if (typeof point.k === 'number' || typeof point.d === 'number') {
        return point;
      }
    }
    return undefined;
  }, [stochRsiData]);

  const latestRoc = useMemo(() => {
    if (!rocData.length) return undefined;
    const last = rocData[rocData.length - 1];
    return typeof last.value === 'number' ? last.value : undefined;
  }, [rocData]);

  const trixData = useMemo<TrixPoint[]>(() => {
    if (!showTrix || cleanOhlc.length === 0) return [];
    const period = 15;
    try {
      const values = TechnicalTRIX.calculate({
        values: closeValues,
        period,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const value = Number(v);
          if (Number.isFinite(value)) {
            return { time, value };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate TRIX', error);
      return [];
    }
  }, [showTrix, cleanOhlc, closeValues, times]);

  const trixByTime = useMemo(() => {
    const map = new Map<number, number>();
    trixData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [trixData]);

  const vwapData = useMemo<VwapPoint[]>(() => {
    if (!showVwap || cleanOhlc.length === 0) return [];
    // Session-based VWAP: reset cumulative sums at each UTC day boundary to better match TradingView session VWAP.
    const result: VwapPoint[] = [];
    let cumulativePv = 0;
    let cumulativeVol = 0;
    let currentSession: string | null = null;

    cleanOhlc.forEach((item) => {
      const time = Number(item.time ?? 0) as UTCTimestamp;
      const dateKey = new Date(time * 1000).toISOString().slice(0, 10); // UTC day
      if (currentSession !== dateKey) {
        currentSession = dateKey;
        cumulativePv = 0;
        cumulativeVol = 0;
      }

      const vol = Number(item.volume ?? 0);
      const typicalPrice =
        (Number(item.high ?? 0) + Number(item.low ?? 0) + Number(item.close ?? 0)) / 3;

      if (Number.isFinite(vol) && Number.isFinite(typicalPrice)) {
        cumulativePv += typicalPrice * vol;
        cumulativeVol += vol;
      }

      const value = cumulativeVol > 0 ? cumulativePv / cumulativeVol : undefined;
      result.push(
        Number.isFinite(value)
          ? { time, value }
          : { time }
      );
    });

    return result;
  }, [showVwap, cleanOhlc]);

  const vwapByTime = useMemo(() => {
    const map = new Map<number, number>();
    vwapData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [vwapData]);

  const latestVwap = useMemo(() => {
    for (let i = vwapData.length - 1; i >= 0; i--) {
      const point = vwapData[i];
      if (typeof point.value === 'number') {
        return point.value;
      }
    }
    return undefined;
  }, [vwapData]);

  const adlData = useMemo<AdlPoint[]>(() => {
    if (!showAdl || cleanOhlc.length === 0) return [];
    const result: AdlPoint[] = [];
    let cumulative = 0;
    cleanOhlc.forEach((item) => {
      const high = Number(item.high ?? 0);
      const low = Number(item.low ?? 0);
      const close = Number(item.close ?? 0);
      const vol = Number(item.volume ?? 0);
      const denom = high - low;
      const mfm = denom === 0 ? 0 : ((close - low) - (high - close)) / denom;
      const mfv = mfm * (Number.isFinite(vol) ? vol : 0);
      if (Number.isFinite(mfv)) {
        cumulative += mfv;
      }
      result.push({ time: Number(item.time ?? 0) as UTCTimestamp, value: cumulative });
    });
    return result.filter((d) => Number.isFinite(d.time));
  }, [showAdl, cleanOhlc]);

  const adlByTime = useMemo(() => {
    const map = new Map<number, number>();
    adlData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [adlData]);

  const latestAdl = useMemo(() => {
    if (!adlData.length) return undefined;
    const last = adlData[adlData.length - 1];
    return typeof last.value === 'number' ? last.value : undefined;
  }, [adlData]);

  const fiData = useMemo<FiPoint[]>(() => {
    if (!showForceIndex || cleanOhlc.length === 0) return [];
    const period = 13;
    try {
      const values = TechnicalForceIndex.calculate({
        close: closeValues,
        volume: volumes,
        period,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const value = Number(v);
          if (Number.isFinite(value)) {
            return { time, value };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate Force Index', error);
      return [];
    }
  }, [showForceIndex, cleanOhlc, closeValues, volumes, times]);

  const fiByTime = useMemo(() => {
    const map = new Map<number, number>();
    fiData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [fiData]);

  const latestFi = useMemo(() => {
    for (let i = fiData.length - 1; i >= 0; i--) {
      const point = fiData[i];
      if (typeof point.value === 'number') {
        return point.value;
      }
    }
    return undefined;
  }, [fiData]);

  const kstData = useMemo<KstPoint[]>(() => {
    if (!showKst || cleanOhlc.length === 0) return [];
    try {
      const values = TechnicalKST.calculate({
        values: closeValues,
        ROCPer1: 10,
        ROCPer2: 15,
        ROCPer3: 20,
        ROCPer4: 30,
        SMAROCPer1: 10,
        SMAROCPer2: 10,
        SMAROCPer3: 10,
        SMAROCPer4: 15,
        signalPeriod: 9,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const kst = typeof v?.kst === 'number' ? Number(v.kst) : undefined;
          const signal = typeof v?.signal === 'number' ? Number(v.signal) : undefined;
          if (kst !== undefined || signal !== undefined) {
            return { time, kst, signal };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate KST', error);
      return [];
    }
  }, [showKst, cleanOhlc, closeValues, times]);

  const kstByTime = useMemo(() => {
    const map = new Map<number, number>();
    kstData.forEach((item) => {
      if (item && typeof item.kst === 'number') {
        map.set(Number(item.time), item.kst);
      }
    });
    return map;
  }, [kstData]);

  const latestKst = useMemo(() => {
    for (let i = kstData.length - 1; i >= 0; i--) {
      const point = kstData[i];
      if (typeof point.kst === 'number' || typeof point.signal === 'number') {
        return point;
      }
    }
    return undefined;
  }, [kstData]);

  const adxData = useMemo<AdxPoint[]>(() => {
    if (!showAdx || cleanOhlc.length === 0) return [];
    const period = 14;
    try {
      const values = TechnicalADX.calculate({
        high: highValues,
        low: lowValues,
        close: closeValues,
        period,
      });
      const offset = closeValues.length - values.length;
      const data = times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          return {
            time,
            adx: typeof v?.adx === 'number' ? Number(v.adx) : undefined,
            pdi: typeof v?.pdi === 'number' ? Number(v.pdi) : undefined,
            mdi: typeof v?.mdi === 'number' ? Number(v.mdi) : undefined,
          };
        }
        return { time };
      });
      // Align ADX timeline to candle timeline using interval (explicit or inferred)
      const latestCandleTime = times.length ? Number(times[times.length - 1]) : null;
      let latestAdxTime: number | null = null;
      for (let i = data.length - 1; i >= 0; i--) {
        if (typeof data[i].adx === 'number' || typeof data[i].pdi === 'number' || typeof data[i].mdi === 'number') {
          latestAdxTime = Number(data[i].time);
          break;
        }
      }
      if (
        latestAdxTime !== null &&
        latestCandleTime !== null &&
        intervalSec > 0 &&
        Number.isFinite(latestAdxTime) &&
        Number.isFinite(latestCandleTime)
      ) {
        const drift = latestAdxTime - latestCandleTime;
        if (Math.abs(drift) > 0) {
          const step = intervalSec;
          const driftSteps = Math.round(drift / step);
          const shift = driftSteps * step;
          if (shift !== 0 && Math.abs(shift) <= step * 48) {
            return data.map((p) => ({
              ...p,
              time: (Number(p.time) - shift) as UTCTimestamp,
            }));
          }
        }
      }
      return data;
    } catch (error) {
      logger.error('Failed to calculate ADX', error);
      return [];
    }
  }, [showAdx, cleanOhlc, highValues, lowValues, closeValues, times, intervalSec]);

  // Debug: log ADX tail to compare with candle times when investigating misalignment
  useEffect(() => {
    if (!showAdx || !adxData.length) return;
    const tail = adxData.slice(-10);
    // eslint-disable-next-line no-console
    console.log('ADX tail (last 10)', tail);
  }, [showAdx, adxData]);

  const latestAdx = useMemo(() => {
    for (let i = adxData.length - 1; i >= 0; i--) {
      const point = adxData[i];
      if (typeof point.adx === 'number') {
        return point;
      }
    }
    return undefined;
  }, [adxData]);

  const aoData = useMemo<AoPoint[]>(() => {
    if (!showAo || cleanOhlc.length === 0) return [];
    try {
      const values = TechnicalAO.calculate({
        high: highValues,
        low: lowValues,
        fastPeriod: 5,
        slowPeriod: 34,
      });
      const offset = highValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const value = Number(v);
          if (Number.isFinite(value)) {
            return { time, value };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate AO', error);
      return [];
    }
  }, [showAo, cleanOhlc, highValues, lowValues, times]);

  const latestAo = useMemo(() => {
    for (let i = aoData.length - 1; i >= 0; i--) {
      const point = aoData[i];
      if (typeof point.value === 'number') {
        return point.value;
      }
    }
    return undefined;
  }, [aoData]);

  const latestTrix = useMemo(() => {
    for (let i = trixData.length - 1; i >= 0; i--) {
      const point = trixData[i];
      if (typeof point.value === 'number') {
        return point.value;
      }
    }
    return undefined;
  }, [trixData]);

  const cciData = useMemo<CciPoint[]>(() => {
    if (!showCci || cleanOhlc.length === 0) return [];
    const period = 20;
    try {
      const values = TechnicalCCI.calculate({
        high: highValues,
        low: lowValues,
        close: closeValues,
        period,
      });
      const offset = closeValues.length - values.length;
      return times.map((time, idx) => {
        if (idx >= offset) {
          const v = values[idx - offset];
          const value = Number(v);
          if (Number.isFinite(value)) {
            return { time, value };
          }
        }
        return { time };
      });
    } catch (error) {
      logger.error('Failed to calculate CCI', error);
      return [];
    }
  }, [showCci, cleanOhlc, highValues, lowValues, closeValues, times]);

  const cciByTime = useMemo(() => {
    const map = new Map<number, number>();
    cciData.forEach((item) => {
      if (item && typeof item.value === 'number') {
        map.set(Number(item.time), item.value);
      }
    });
    return map;
  }, [cciData]);

  const latestCci = useMemo(() => {
    for (let i = cciData.length - 1; i >= 0; i--) {
      const point = cciData[i];
      if (typeof point.value === 'number') {
        return point.value;
      }
    }
    return undefined;
  }, [cciData]);

  useEffect(() => {
    const charts: Array<IChartApi | null> = [
      mainChart.current,
      volumeChart.current,
      macdChart.current,
      obvChart.current,
      williamsChart.current,
      atrChart.current,
      mfiChart.current,
      stochasticChart.current,
      rocChart.current,
      trixChart.current,
      adlChart.current,
      fiChart.current,
      kstChart.current,
      stochRsiChart.current,
      ...Object.values(overlayPaneCharts.current),
      adxChart.current,
      aoChart.current,
      cciChart.current,
    ];

    const widths = charts
      .map((chart) => chart?.priceScale('right').width() ?? 0)
      .filter((width) => width > 0);

    if (widths.length === 0) return;

    const maxWidth = Math.max(...widths);
    if (!Number.isFinite(maxWidth) || maxWidth <= 0) return;
    if (priceScaleWidthRef.current === maxWidth) return;

    priceScaleWidthRef.current = maxWidth;
    charts.forEach((chart) => {
      if (!chart) return;
      chart.priceScale('right').applyOptions({ minimumWidth: maxWidth });
    });
  }, [
    containerWidth,
    showVolume,
    showRsi,
    showMacd,
    showObv,
    showWilliamsR,
    showAtr,
    showMfi,
    showStochastic,
    showStochRsi,
    showRoc,
    showTrix,
    showAdl,
    showForceIndex,
    showKst,
    showStochRsi,
    showVolumeProfile,
    showAdx,
    showAo,
    showCci,
    theme,
    latestMacd,
    latestWilliams,
    latestAtr,
    latestMfi,
    latestRoc,
    latestTrix,
    latestAdl,
    latestFi,
    latestKst,
    latestStochRsi,
    latestAdx,
    latestAo,
    latestCci,
  ]);

  useEffect(() => {
    if (!mainChart.current || (!showVolumeProfile && !showIchimokuCloud)) return;
    const scale = mainChart.current.timeScale();
    const handleRangeChange = () => {
      setVolumeProfileVersion((prev) => prev + 1);
    };
    scale.subscribeVisibleLogicalRangeChange(handleRangeChange);
    handleRangeChange();
    return () => {
      scale.unsubscribeVisibleLogicalRangeChange(handleRangeChange);
    };
  }, [showVolumeProfile, showIchimokuCloud]);

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newWidth = Math.floor(rect.width - 20);
        if (newWidth > 200 && newWidth !== containerWidth) {
          setContainerWidth(newWidth);
        }
      }
    };

    updateContainerWidth();

    const handleResize = () => {
      setTimeout(updateContainerWidth, 100);
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver: ResizeObserver | null = null;
    if ((window as any).ResizeObserver && containerRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [containerWidth]);

  useImperativeHandle(ref, () => ({
    getCanvas: () => {
      return mainChartRef.current?.querySelector("canvas") || null;
    },
    getChartApi: () => mainChart.current,
    getCandleSeries: () => candleSeriesRef.current,
    toggleRectangleTool: handleRectToolToggle,
    togglePointTool: handlePointToolToggle,
    clearRectangle: handleClearRect,
    clearPoints: handleClearPoints,
    setRectangleSelection: (rect: { startIndex: number; endIndex: number }) => {
      if (!rect || typeof rect.startIndex !== 'number' || typeof rect.endIndex !== 'number') return;
      // Always update selection state (for analysis panel) even if chart isn't ready yet.
      try {
        const left = Math.max(0, Math.min(rect.startIndex, rect.endIndex));
        const right = Math.min(ohlcData.length - 1, Math.max(rect.startIndex, rect.endIndex));
        if (ohlcData && ohlcData.length > 0 && right >= left) {
          const selectedData = ohlcData.slice(left, right + 1);
          const prices = selectedData
            .map((item: any) => [item?.high, item?.low])
            .flat()
            .filter((v: any) => Number.isFinite(v));
          const top = prices.length ? Math.max(...prices) : undefined;
          const bottom = prices.length ? Math.min(...prices) : undefined;
          handleRectChange({
            startIndex: left,
            endIndex: right,
            startTime: ohlcData[left]?.time,
            endTime: ohlcData[right]?.time,
            startPrice: bottom,
            endPrice: top,
            selectedData
          });
        }
      } catch {
        // ignore
      }

      if (rectangleToolRef.current?.setAreaByIndex) {
        // Ensure rectangle mode is active for consistent UX
        setRectToolActive(true);
        setPointToolActive(false);
        setActiveTool('rectangle');
        if (mainChart.current) {
          mainChart.current.applyOptions({
            handleScroll: false,
            handleScale: false,
          });
        }
        rectangleToolRef.current.setAreaByIndex(rect.startIndex, rect.endIndex);
      }
    },
    setTradeMarkers: (markers: TradeMarker[]) => {
      if (candleSeriesRef.current) {
        const chartMarkers = markers.map(m => ({
          time: m.time as UTCTimestamp,
          position: m.type === 'buy' ? 'belowBar' as const : 'aboveBar' as const,
          color: m.type === 'buy' ? '#52c41a' : '#ff4d4f',
          shape: m.type === 'buy' ? 'arrowUp' as const : 'arrowDown' as const,
          text: m.label || (m.type === 'buy' ? 'B' : 'S'),
          size: 1.5
        }));
        candleSeriesRef.current.setMarkers(chartMarkers);
      }
    },
    clearTradeMarkers: () => {
      if (candleSeriesRef.current) {
        candleSeriesRef.current.setMarkers([]);
      }
    },
    scrollToTime: (timestamp: number) => {
      if (mainChart.current) {
        const timeScale = mainChart.current.timeScale();
        // Get visible range to maintain zoom level
        const visibleRange = timeScale.getVisibleRange();
        if (visibleRange) {
          const rangeWidth = (visibleRange.to as number) - (visibleRange.from as number);
          // Center the view on the timestamp
          const newFrom = timestamp - rangeWidth / 2;
          const newTo = timestamp + rangeWidth / 2;
          timeScale.setVisibleRange({
            from: newFrom as UTCTimestamp,
            to: newTo as UTCTimestamp
          });
        } else {
          // If no visible range, just scroll to position
          timeScale.scrollToPosition(-10, false);
        }
      }
    },
    setPointMarker: (time: number, price: number) => {
      // 使用 PointTool 的 setPoint 方法来显示点（和用户手动点击一样的效果）
      if (pointToolRef.current?.setPoint) {
        // Agent/外部选择点时：强制进入点工具模式（保证按钮高亮一致）
          setPointToolActive(true);
          setRectToolActive(false);
          setActiveTool('point');
        if (mainChart.current) {
          mainChart.current.applyOptions({
            handleScroll: false,
            handleScale: false,
          });
        }
        // 设置点
        pointToolRef.current.setPoint(time, price);
      }
    },
  }));

  useEffect(() => {
    if (mainChartRef.current && !mainChart.current) {
      mainChart.current = createChart(mainChartRef.current, {
        width: getActualWidth(),
        height: mainHeight,
        layout: {
          background: { color: theme.chartBackground },
          textColor: theme.chartText,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          fixLeftEdge: false,
          fixRightEdge: false,
          timeVisible: true,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: { borderColor: theme.chartPriceScaleBorder },
        leftPriceScale: { visible: false, borderColor: theme.chartPriceScaleBorder, alignLabels: true },
      });

      const candleSeries = mainChart.current.addCandlestickSeries({
        upColor: theme.candleUp,
        downColor: theme.candleDown,
        borderVisible: false,
        wickUpColor: theme.candleUp,
        wickDownColor: theme.candleDown,
      });

      candleSeriesRef.current = candleSeries;
      areaSeriesRef.current = mainChart.current.addAreaSeries({
        lineColor: 'rgba(0,0,0,0)',
        topColor: theme.areaTop,
        bottomColor: theme.areaBottom,
        priceLineVisible: false,
      });

      if (ohlcData.length > 0) {
        candleSeries.setData(ohlcData);
        mainChart.current.timeScale().scrollToRealTime();
        isInitialDataSet.current = true;
      }

      mainChart.current.applyOptions({
        handleScroll: !activeTool,
        handleScale: !activeTool,
      });

      // Add crosshair move handler for tooltip
      mainChart.current.subscribeCrosshairMove((param) => {
        if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
          setTooltip(prev => ({ ...prev, visible: false }));
          setSharedCrosshairLogical(null);
          setSharedCrosshairTime(null);
          setSharedCrosshairPrice(null);
          return;
        }

        if (typeof param.logical === 'number') {
          setSharedCrosshairLogical(param.logical);
        } else if (param.time) {
          const logical = mainChart.current?.timeScale().coordinateToLogical(param.point.x);
          if (typeof logical === 'number') {
            setSharedCrosshairLogical(logical);
          }
        }
        if (param.time) {
          setSharedCrosshairTime(param.time as number);
          const ohlc = ohlcByTime.get(Number(param.time));
          if (ohlc && Number.isFinite(ohlc.close)) {
            setSharedCrosshairPrice(Number(ohlc.close));
          }
        } else {
          const time = mainChart.current?.timeScale().coordinateToTime(
            param.point.x
          ) as UTCTimestamp | undefined;
          setSharedCrosshairTime(typeof time === 'number' ? time : null);
          if (typeof time === 'number') {
            const ohlc = ohlcByTime.get(Number(time));
            if (ohlc && Number.isFinite(ohlc.close)) {
              setSharedCrosshairPrice(Number(ohlc.close));
            }
          }
        }

        const data = param.seriesData.get(candleSeries);
        if (data && typeof data === 'object' && 'time' in data && 'open' in data && 'high' in data && 'low' in data && 'close' in data) {
          const time = data.time as number;
          const price = data.close as number;
          const ohlc = {
            open: data.open as number,
            high: data.high as number,
            low: data.low as number,
            close: data.close as number
          };

          setTooltip({
            visible: true,
            x: param.point.x,
            y: param.point.y,
            time: formatTimestampToEST(time),
            price: price.toFixed(2),
            ohlc: ohlc
          });
        }
      });
    }

    return () => {
      mainChart.current?.remove();
      volumeChart.current?.remove();
      macdChart.current?.remove();
      williamsChart.current?.remove();
      atrChart.current?.remove();
      mfiChart.current?.remove();
      stochasticChart.current?.remove();
      rocChart.current?.remove();
      trixChart.current?.remove();
      adlChart.current?.remove();
      adxChart.current?.remove();
      aoChart.current?.remove();
      cciChart.current?.remove();
      fiChart.current?.remove();
      kstChart.current?.remove();
      stochRsiChart.current?.remove();
      volumeChart.current = null;
      macdChart.current = null;
      williamsChart.current = null;
      atrChart.current = null;
      mfiChart.current = null;
      stochasticChart.current = null;
      rocChart.current = null;
      trixChart.current = null;
      adlChart.current = null;
      adxChart.current = null;
      aoChart.current = null;
      cciChart.current = null;
      fiChart.current = null;
      kstChart.current = null;
      stochRsiChart.current = null;
      volumeSeriesRef.current = null;
      macdHistogramSeriesRef.current = null;
      macdLineSeriesRef.current = null;
      macdSignalSeriesRef.current = null;
      williamsSeriesRef.current = null;
      atrSeriesRef.current = null;
      mfiSeriesRef.current = null;
      stochasticKSeriesRef.current = null;
      stochasticDSeriesRef.current = null;
      rocSeriesRef.current = null;
      trixSeriesRef.current = null;
      vwapSeriesRef.current = null;
      adlSeriesRef.current = null;
      adxSeriesRef.current = null;
      adxPlusDiSeriesRef.current = null;
      adxMinusDiSeriesRef.current = null;
      cciSeriesRef.current = null;
      aoSeriesRef.current = null;
      fiSeriesRef.current = null;
      kstSeriesRef.current = null;
      kstSignalSeriesRef.current = null;
      stochRsiKSeriesRef.current = null;
      stochRsiDSeriesRef.current = null;
      mainChart.current = null;
      isInitialDataSet.current = false;
    };
  }, [mainHeight, subHeight, theme]);

  // Track whether user has detached from the right edge to suppress auto scroll-to-realtime
  useEffect(() => {
    if (!mainChart.current || !candleSeriesRef.current) return;
    const scale = mainChart.current.timeScale();
    const handler = () => {
      const range = scale.getVisibleLogicalRange();
      if (!range) return;
      const barsInfo = candleSeriesRef.current?.barsInLogicalRange(range);
      if (!barsInfo || typeof barsInfo.barsAfter !== 'number') return;
      userDetachedFromRightRef.current = barsInfo.barsAfter > 0;
    };
    scale.subscribeVisibleLogicalRangeChange(handler);
    handler();
    return () => {
      try {
        scale.unsubscribeVisibleLogicalRangeChange(handler);
      } catch {
        // ignore
      }
    };
  }, [ohlcData.length]);

  useEffect(() => {
    const actualWidth = getActualWidth();
    if (mainChart.current) mainChart.current.resize(actualWidth, mainHeight);
    if (macdChart.current) macdChart.current.resize(actualWidth, subHeight);
    if (volumeChart.current) volumeChart.current.resize(actualWidth, subHeight);
    if (obvChart.current) obvChart.current.resize(actualWidth, subHeight);
    if (williamsChart.current) williamsChart.current.resize(actualWidth, subHeight);
    if (atrChart.current) atrChart.current.resize(actualWidth, subHeight);
    if (mfiChart.current) mfiChart.current.resize(actualWidth, subHeight);
    if (stochasticChart.current) stochasticChart.current.resize(actualWidth, subHeight);
    if (rocChart.current) rocChart.current.resize(actualWidth, subHeight);
    if (trixChart.current) trixChart.current.resize(actualWidth, subHeight);
    if (adlChart.current) adlChart.current.resize(actualWidth, subHeight);
    if (adxChart.current) adxChart.current.resize(actualWidth, subHeight);
    if (aoChart.current) aoChart.current.resize(actualWidth, subHeight);
    if (cciChart.current) cciChart.current.resize(actualWidth, subHeight);
    if (fiChart.current) fiChart.current.resize(actualWidth, subHeight);
    if (kstChart.current) kstChart.current.resize(actualWidth, subHeight);
    if (stochRsiChart.current) stochRsiChart.current.resize(actualWidth, subHeight);
  }, [containerWidth, width, mainHeight, subHeight]);

  useEffect(() => {
    if (!showVolume) {
      if (volumeChart.current) {
        safeRemoveChart(volumeChart, [volumeSeriesRef]);
      }
      return;
    }

    if (!volumeChartRef.current) return;
    if (!volumeChart.current) {
      volumeChart.current = createChart(volumeChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
          tickMarkFormatter: sharedTickFormatter,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      volumeChart.current.priceScale('right').applyOptions({
        // Give extra bottom padding so the lowest volume label isn't clipped
        scaleMargins: { top: 0.05, bottom: 0.12 },
      });

      volumeSeriesRef.current = volumeChart.current.addHistogramSeries({
        priceLineVisible: false,
        baseLineVisible: false,
        priceFormat: { type: 'volume' },
      });
    } else {
      volumeChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showVolume, subHeight, theme]);

  // OBV chart (sub-pane)
  useEffect(() => {
    if (!showObv) {
      if (obvChart.current) {
        safeRemoveChart(obvChart, [obvSeriesRef]);
      }
      return;
    }

    if (!obvChartRef.current) return;
    if (!obvChart.current) {
      obvChart.current = createChart(obvChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      obvSeriesRef.current = obvChart.current.addLineSeries({
        color: '#ffa940',
        lineWidth: 2,
        priceLineVisible: false,
      });

      obvChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      obvChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showObv, subHeight, theme]);

  // ADL chart (sub-pane)
  useEffect(() => {
    if (!showAdl) {
      if (adlChart.current) {
        safeRemoveChart(adlChart, [adlSeriesRef]);
      }
      return;
    }

    if (!adlChartRef.current) return;
    if (!adlChart.current) {
      adlChart.current = createChart(adlChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      adlSeriesRef.current = adlChart.current.addLineSeries({
        color: '#177ddc',
        lineWidth: 2,
        priceLineVisible: false,
      });

      adlChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      adlChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showAdl, subHeight, theme]);

  // Force Index chart (sub-pane)
  useEffect(() => {
    if (!showForceIndex) {
      if (fiChart.current) {
        safeRemoveChart(fiChart, [fiSeriesRef]);
      }
      return;
    }

    if (!fiChartRef.current) return;
    if (!fiChart.current) {
      fiChart.current = createChart(fiChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      fiSeriesRef.current = fiChart.current.addLineSeries({
        color: '#fa8c16',
        lineWidth: 2,
        priceLineVisible: false,
      });

      fiChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      fiChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showForceIndex, subHeight, theme]);

  // KST chart (sub-pane)
  useEffect(() => {
    if (!showKst) {
      if (kstChart.current) {
        safeRemoveChart(kstChart, [kstSeriesRef, kstSignalSeriesRef]);
      }
      return;
    }

    if (!kstChartRef.current) return;
    if (!kstChart.current) {
      kstChart.current = createChart(kstChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      kstSeriesRef.current = kstChart.current.addLineSeries({
        color: '#2f54eb',
        lineWidth: 2,
        priceLineVisible: false,
      });
      kstSignalSeriesRef.current = kstChart.current.addLineSeries({
        color: '#13c2c2',
        lineWidth: 2,
        priceLineVisible: false,
      });

      kstChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      kstChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showKst, subHeight, theme]);

  // ADX chart (sub-pane)
  useEffect(() => {
    if (!showAdx) {
      if (adxChart.current) {
        safeRemoveChart(adxChart, [adxSeriesRef, adxPlusDiSeriesRef, adxMinusDiSeriesRef]);
      }
      return;
    }

    if (!adxChartRef.current) return;
    if (!adxChart.current) {
      adxChart.current = createChart(adxChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      adxSeriesRef.current = adxChart.current.addLineSeries({
        color: '#52c41a',
        lineWidth: 2,
        priceLineVisible: false,
      });
      adxPlusDiSeriesRef.current = adxChart.current.addLineSeries({
        color: '#13c2c2',
        lineWidth: 2,
        priceLineVisible: false,
      });
      adxMinusDiSeriesRef.current = adxChart.current.addLineSeries({
        color: '#f5222d',
        lineWidth: 2,
        priceLineVisible: false,
      });

      adxChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      adxChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
        timeScale: {
          tickMarkFormatter: sharedTickFormatter,
        },
      });
    }
    // ensure formatter stays applied even if chart already existed
    adxChart.current?.applyOptions({
      timeScale: {
        tickMarkFormatter: sharedTickFormatter,
      },
    });
    // Snap ADX pane to the same visible logical range as main chart
    if (mainChart.current && adxChart.current) {
      const range = mainChart.current.timeScale().getVisibleLogicalRange();
      if (range && typeof range.from === 'number' && typeof range.to === 'number') {
        try {
          adxChart.current.timeScale().setVisibleLogicalRange(range);
        } catch {}
      }
    }
  }, [showAdx, subHeight, theme, sharedTickFormatter]);

  // ATR chart (sub-pane)
  useEffect(() => {
    if (!showAtr) {
      if (atrChart.current) {
        safeRemoveChart(atrChart, [atrSeriesRef]);
      }
      return;
    }

    if (!atrChartRef.current) return;
    if (!atrChart.current) {
      atrChart.current = createChart(atrChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      atrSeriesRef.current = atrChart.current.addLineSeries({
        color: '#1890ff',
        lineWidth: 2,
        priceLineVisible: false,
      });

      atrChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      atrChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showAtr, subHeight, theme]);

  // MFI chart (sub-pane)
  useEffect(() => {
    if (!showMfi) {
      if (mfiChart.current) {
        const chart = mfiChart.current;
        mfiChart.current = null;
        mfiSeriesRef.current = null;
        try {
          chart.remove();
        } catch {}
      }
      return;
    }

    if (!mfiChartRef.current) return;
    if (!mfiChart.current) {
      mfiChart.current = createChart(mfiChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      mfiSeriesRef.current = mfiChart.current.addLineSeries({
        color: '#52c41a',
        lineWidth: 2,
        priceLineVisible: false,
      });

      mfiChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      mfiChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showMfi, subHeight, theme]);

  // Stochastic chart (sub-pane)
  useEffect(() => {
    if (!showStochastic) {
      if (stochasticChart.current) {
        safeRemoveChart(stochasticChart, [stochasticKSeriesRef, stochasticDSeriesRef]);
      }
      return;
    }

    if (!stochasticChartRef.current) return;
    if (!stochasticChart.current) {
      stochasticChart.current = createChart(stochasticChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      stochasticKSeriesRef.current = stochasticChart.current.addLineSeries({
        color: '#722ed1',
        lineWidth: 2,
        priceLineVisible: false,
      });

      stochasticDSeriesRef.current = stochasticChart.current.addLineSeries({
        color: '#13c2c2',
        lineWidth: 2,
        priceLineVisible: false,
      });

      stochasticChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      stochasticChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showStochastic, subHeight, theme]);

  // StochRSI chart (sub-pane)
  useEffect(() => {
    if (!showStochRsi) {
      if (stochRsiChart.current) {
        safeRemoveChart(stochRsiChart, [stochRsiKSeriesRef, stochRsiDSeriesRef]);
      }
      return;
    }

    if (!stochRsiChartRef.current) return;
    if (!stochRsiChart.current) {
      stochRsiChart.current = createChart(stochRsiChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      stochRsiKSeriesRef.current = stochRsiChart.current.addLineSeries({
        color: '#722ed1',
        lineWidth: 2,
        priceLineVisible: false,
      });

      stochRsiDSeriesRef.current = stochRsiChart.current.addLineSeries({
        color: '#13c2c2',
        lineWidth: 2,
        priceLineVisible: false,
      });

      stochRsiChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      stochRsiChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showStochRsi, subHeight, theme]);

  // ROC chart (sub-pane)
  useEffect(() => {
    if (!showRoc) {
      if (rocChart.current) {
        safeRemoveChart(rocChart, [rocSeriesRef]);
      }
      return;
    }

    if (!rocChartRef.current) return;
    if (!rocChart.current) {
      rocChart.current = createChart(rocChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      rocSeriesRef.current = rocChart.current.addLineSeries({
        color: '#08979c',
        lineWidth: 2,
        priceLineVisible: false,
      });

      rocChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      rocChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showRoc, subHeight, theme]);

  // TRIX chart (sub-pane)
  useEffect(() => {
    if (!showTrix) {
      if (trixChart.current) {
        safeRemoveChart(trixChart, [trixSeriesRef]);
      }
      return;
    }

    if (!trixChartRef.current) return;
    if (!trixChart.current) {
      trixChart.current = createChart(trixChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      trixSeriesRef.current = trixChart.current.addLineSeries({
        color: '#eb2f96',
        lineWidth: 2,
        priceLineVisible: false,
      });

      trixChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      trixChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showTrix, subHeight, theme]);

  // AO chart (sub-pane)
  useEffect(() => {
    if (!showAo) {
      if (aoChart.current) {
        safeRemoveChart(aoChart, [aoSeriesRef]);
      }
      return;
    }

    if (!aoChartRef.current) return;
    if (!aoChart.current) {
      aoChart.current = createChart(aoChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      aoSeriesRef.current = aoChart.current.addHistogramSeries({
        priceLineVisible: false,
        baseLineVisible: true,
        base: 0,
      });

      aoChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      aoChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showAo, subHeight, theme]);

  // CCI chart (sub-pane)
  useEffect(() => {
    if (!showCci) {
      if (cciChart.current) {
        safeRemoveChart(cciChart, [cciSeriesRef]);
      }
      return;
    }

    if (!cciChartRef.current) return;
    if (!cciChart.current) {
      cciChart.current = createChart(cciChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      cciSeriesRef.current = cciChart.current.addLineSeries({
        color: '#722ed1',
        lineWidth: 2,
        priceLineVisible: false,
      });

      cciChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      cciChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showCci, subHeight, theme]);

  // Williams %R chart (sub-pane)
  useEffect(() => {
    if (!showWilliamsR) {
      if (williamsChart.current) {
        safeRemoveChart(williamsChart, [williamsSeriesRef]);
      }
      return;
    }

    if (!williamsChartRef.current) return;
    if (!williamsChart.current) {
      williamsChart.current = createChart(williamsChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      williamsSeriesRef.current = williamsChart.current.addLineSeries({
        color: '#fadb14',
        lineWidth: 2,
        priceLineVisible: false,
      });

      williamsChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    } else {
      williamsChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }
  }, [showWilliamsR, subHeight, theme]);

  useEffect(() => {
    if (!showVolume || !volumeSeriesRef.current) return;
    if (!ohlcData || ohlcData.length === 0) {
      volumeSeriesRef.current.setData([]);
      return;
    }

    const histogramData = ohlcData.map((item) => {
      const value = Number(item?.volume ?? item?.vol ?? 0);
      const isUp = (item?.close ?? 0) >= (item?.open ?? 0);
      return {
        time: item.time,
        value: Number.isFinite(value) ? value : 0,
        color: isUp ? theme.candleUp : theme.candleDown,
      };
    });

    try {
      volumeSeriesRef.current.setData(histogramData);
      scrollToRealTimeIfLive(volumeChart.current);
    } catch (err) {
      logger.error('Failed to draw volume series', err);
    }
  }, [showVolume, ohlcData, theme]);

  useEffect(() => {
    if (!showObv || !obvSeriesRef.current) return;
    if (!obvData.length) {
      obvSeriesRef.current.setData([]);
      return;
    }
    try {
      obvSeriesRef.current.setData(obvData);
      scrollToRealTimeIfLive(obvChart.current);
    } catch (err) {
      logger.error('Failed to draw OBV series', err);
    }
  }, [showObv, obvData]);

  useEffect(() => {
    if (!showAtr || !atrSeriesRef.current) return;
    if (!atrData.length) {
      atrSeriesRef.current.setData([]);
      return;
    }
    try {
      atrSeriesRef.current.setData(atrData);
      scrollToRealTimeIfLive(atrChart.current);
    } catch (err) {
      logger.error('Failed to draw ATR series', err);
    }
  }, [showAtr, atrData]);

  useEffect(() => {
    if (!showMfi || !mfiSeriesRef.current) return;
    if (!mfiData.length) {
      mfiSeriesRef.current.setData([]);
      return;
    }
    try {
      mfiSeriesRef.current.setData(mfiData);
      scrollToRealTimeIfLive(mfiChart.current);
    } catch (err) {
      logger.error('Failed to draw MFI series', err);
    }
  }, [showMfi, mfiData]);

  useEffect(() => {
    if (!showStochastic || (!stochasticKSeriesRef.current && !stochasticDSeriesRef.current)) return;
    if (!stochasticData.length) {
      stochasticKSeriesRef.current?.setData([]);
      stochasticDSeriesRef.current?.setData([]);
      return;
    }
    const kData = stochasticData.map((p) =>
      typeof p.k === 'number' ? { time: p.time, value: p.k } : { time: p.time }
    );
    const dData = stochasticData.map((p) =>
      typeof p.d === 'number' ? { time: p.time, value: p.d } : { time: p.time }
    );
    try {
      stochasticKSeriesRef.current?.setData(kData as any);
      stochasticDSeriesRef.current?.setData(dData as any);
      scrollToRealTimeIfLive(stochasticChart.current);
    } catch (err) {
      logger.error('Failed to draw Stochastic series', err);
    }
  }, [showStochastic, stochasticData]);

  useEffect(() => {
    if (!showStochRsi || (!stochRsiKSeriesRef.current && !stochRsiDSeriesRef.current)) return;
    if (!stochRsiData.length) {
      stochRsiKSeriesRef.current?.setData([]);
      stochRsiDSeriesRef.current?.setData([]);
      return;
    }
    const kData = stochRsiData.map((p) =>
      typeof p.k === 'number' ? { time: p.time, value: p.k } : { time: p.time }
    );
    const dData = stochRsiData.map((p) =>
      typeof p.d === 'number' ? { time: p.time, value: p.d } : { time: p.time }
    );
    try {
      stochRsiKSeriesRef.current?.setData(kData as any);
      stochRsiDSeriesRef.current?.setData(dData as any);
      scrollToRealTimeIfLive(stochRsiChart.current);
    } catch (err) {
      logger.error('Failed to draw StochRSI series', err);
    }
  }, [showStochRsi, stochRsiData]);

  useEffect(() => {
    if (!showRoc || !rocSeriesRef.current) return;
    if (!rocData.length) {
      rocSeriesRef.current.setData([]);
      return;
    }
    try {
      rocSeriesRef.current.setData(rocData);
      scrollToRealTimeIfLive(rocChart.current);
    } catch (err) {
      logger.error('Failed to draw ROC series', err);
    }
  }, [showRoc, rocData]);

  useEffect(() => {
    if (!showTrix || !trixSeriesRef.current) return;
    if (!trixData.length) {
      trixSeriesRef.current.setData([]);
      return;
    }
    try {
      trixSeriesRef.current.setData(trixData);
      scrollToRealTimeIfLive(trixChart.current);
    } catch (err) {
      logger.error('Failed to draw TRIX series', err);
    }
  }, [showTrix, trixData]);

  useEffect(() => {
    if (!showAdl || !adlSeriesRef.current) return;
    if (!adlData.length) {
      adlSeriesRef.current.setData([]);
      return;
    }
    try {
      adlSeriesRef.current.setData(adlData);
      scrollToRealTimeIfLive(adlChart.current);
    } catch (err) {
      logger.error('Failed to draw ADL series', err);
    }
  }, [showAdl, adlData]);

  useEffect(() => {
    if (!showForceIndex || !fiSeriesRef.current) return;
    if (!fiData.length) {
      fiSeriesRef.current.setData([]);
      return;
    }
    try {
      fiSeriesRef.current.setData(fiData);
      scrollToRealTimeIfLive(fiChart.current);
    } catch (err) {
      logger.error('Failed to draw Force Index series', err);
    }
  }, [showForceIndex, fiData]);

  useEffect(() => {
    if (!showKst || (!kstSeriesRef.current && !kstSignalSeriesRef.current)) return;
    if (!kstData.length) {
      kstSeriesRef.current?.setData([]);
      kstSignalSeriesRef.current?.setData([]);
      return;
    }
    const kstLine = kstData.map((p) =>
      typeof p.kst === 'number' ? { time: p.time, value: p.kst } : { time: p.time }
    );
    const signalLine = kstData.map((p) =>
      typeof p.signal === 'number' ? { time: p.time, value: p.signal } : { time: p.time }
    );
    try {
      kstSeriesRef.current?.setData(kstLine as any);
      kstSignalSeriesRef.current?.setData(signalLine as any);
      scrollToRealTimeIfLive(kstChart.current);
    } catch (err) {
      logger.error('Failed to draw KST series', err);
    }
  }, [showKst, kstData]);

  useEffect(() => {
    if (!showAdx || (!adxSeriesRef.current && !adxPlusDiSeriesRef.current && !adxMinusDiSeriesRef.current)) return;
    if (!adxData.length) {
      adxSeriesRef.current?.setData([]);
      adxPlusDiSeriesRef.current?.setData([]);
      adxMinusDiSeriesRef.current?.setData([]);
      return;
    }

    const adxLine = adxData.map((p) =>
      typeof p.adx === 'number' ? { time: p.time, value: p.adx } : { time: p.time }
    );
    const plusDiLine = adxData.map((p) =>
      typeof p.pdi === 'number' ? { time: p.time, value: p.pdi } : { time: p.time }
    );
    const minusDiLine = adxData.map((p) =>
      typeof p.mdi === 'number' ? { time: p.time, value: p.mdi } : { time: p.time }
    );

    try {
      adxSeriesRef.current?.setData(adxLine as any);
      adxPlusDiSeriesRef.current?.setData(plusDiLine as any);
      adxMinusDiSeriesRef.current?.setData(minusDiLine as any);
      scrollToRealTimeIfLive(adxChart.current);
    } catch (err) {
      logger.error('Failed to draw ADX series', err);
    }
  }, [showAdx, adxData]);

  useEffect(() => {
    if (!showAo || !aoSeriesRef.current) return;
    if (!aoData.length) {
      aoSeriesRef.current.setData([]);
      return;
    }
    const aoHist = aoData.map((p) => ({
      time: p.time,
      value: p.value ?? 0,
      color: (p.value ?? 0) >= 0 ? theme.candleUp : theme.candleDown,
    }));
    try {
      aoSeriesRef.current.setData(aoHist);
      scrollToRealTimeIfLive(aoChart.current);
    } catch (err) {
      logger.error('Failed to draw AO series', err);
    }
  }, [showAo, aoData, theme]);

  useEffect(() => {
    if (!showCci || !cciSeriesRef.current) return;
    if (!cciData.length) {
      cciSeriesRef.current.setData([]);
      return;
    }
    try {
      cciSeriesRef.current.setData(cciData);
      scrollToRealTimeIfLive(cciChart.current);
    } catch (err) {
      logger.error('Failed to draw CCI series', err);
    }
  }, [showCci, cciData]);

  useEffect(() => {
    if (!showWilliamsR || !williamsSeriesRef.current) return;
    if (!williamsData.length) {
      williamsSeriesRef.current.setData([]);
      return;
    }
    try {
      williamsSeriesRef.current.setData(williamsData);
      scrollToRealTimeIfLive(williamsChart.current);
    } catch (err) {
      logger.error('Failed to draw Williams %R series', err);
    }
  }, [showWilliamsR, williamsData]);

  useEffect(() => {
    if (!showVolume || !volumeChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const volumeScale = volumeChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (volumeSyncGuardRef.current || !range) return;
      volumeSyncGuardRef.current = true;
      volumeScale.setVisibleLogicalRange(range);
      volumeSyncGuardRef.current = false;
    };

    const syncFromVolume = (range: any) => {
      if (volumeSyncGuardRef.current || !range) return;
      volumeSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      volumeSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    volumeScale.subscribeVisibleLogicalRangeChange(syncFromVolume);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      volumeScale.unsubscribeVisibleLogicalRangeChange(syncFromVolume);
    };
  }, [showVolume]);

  useEffect(() => {
    if (!showMacd) {
      if (macdChart.current) {
        safeRemoveChart(macdChart, [macdHistogramSeriesRef, macdLineSeriesRef, macdSignalSeriesRef]);
      }
      return;
    }

    if (!macdChartRef.current) return;
    if (!macdChart.current) {
      macdChart.current = createChart(macdChartRef.current, {
        width: getActualWidth(),
        height: subHeight,
        layout: {
          background: { color: theme.surfaceBackground },
          textColor: theme.textSecondary,
        },
        timeScale: {
          barSpacing: 12,
          minBarSpacing: 8,
          timeVisible: true,
          borderColor: theme.chartGrid,
        },
        grid: {
          vertLines: { color: theme.chartGrid },
          horzLines: { color: theme.chartGrid },
        },
        rightPriceScale: {
          borderColor: theme.chartPriceScaleBorder,
        },
      });

      macdHistogramSeriesRef.current = macdChart.current.addHistogramSeries({
        priceLineVisible: false,
        baseLineVisible: false,
      });

      macdLineSeriesRef.current = macdChart.current.addLineSeries({
        color: '#ff9800',
        lineWidth: 2,
        priceLineVisible: false,
      });

      macdSignalSeriesRef.current = macdChart.current.addLineSeries({
        color: '#4caf50',
        lineWidth: 2,
        priceLineVisible: false,
      });
    } else {
      macdChart.current.applyOptions({
        width: getActualWidth(),
        height: subHeight,
      });
    }

    if (macdChart.current) {
      macdChart.current.priceScale('right').applyOptions({
        scaleMargins: { top: 0.15, bottom: 0.05 },
        borderColor: theme.chartPriceScaleBorder,
      });
    }
  }, [showMacd, subHeight, theme]);

  const teardownOverlay = useCallback((key: string) => {
    const entry = overlaySeriesRef.current[key];
    if (entry) {
      const chart =
        entry.target === 'overlayPane'
          ? overlayPaneCharts.current[entry.baseId]
          : mainChart.current;
      if (chart) {
        chart.removeSeries(entry.series);
      }
    }
    delete overlaySeriesRef.current[key];
  }, []);

  const getPlacement = React.useCallback(
    (indicatorId: IndicatorId) => {
      if (indicatorId === 'volumeProfile') return 'main';
      return indicatorPlacements[indicatorId] ?? 'main';
    },
    [indicatorPlacements]
  );

  const overlayLegend = useMemo(
    () =>
      [
        { id: 'rsi', label: 'RSI', color: '#9c27b0', enabled: showRsi, placement: getPlacement('rsi') },
        { id: 'sma', label: 'SMA', color: '#13c2c2', enabled: showSma, placement: getPlacement('sma') },
        { id: 'ema', label: 'EMA', color: '#52c41a', enabled: showEma, placement: getPlacement('ema') },
        { id: 'wma', label: 'WMA', color: '#2f54eb', enabled: showWma, placement: getPlacement('wma') },
        { id: 'wema', label: 'WEMA', color: '#fa8c16', enabled: showWema, placement: getPlacement('wema') },
        { id: 'typicalPrice', label: 'Typical Price', color: '#eb2f96', enabled: showTypicalPrice, placement: getPlacement('typicalPrice') },
        { id: 'bollingerBands', label: 'BB (20,2)', color: '#722ed1', enabled: showBollingerBands, placement: getPlacement('bollingerBands') },
        { id: 'psar', label: 'PSAR', color: '#fa541c', enabled: showPsar, placement: getPlacement('psar') },
        { id: 'volumeProfile', label: 'Volume Profile', color: '#d4b106', enabled: showVolumeProfile, placement: getPlacement('volumeProfile') },
        { id: 'ichimokuCloud', label: 'Ichimoku Cloud', color: '#52c41a', enabled: showIchimokuCloud, placement: getPlacement('ichimokuCloud') },
        { id: 'vwap', label: 'VWAP', color: '#d4b106', enabled: showVwap, placement: getPlacement('vwap') },
      ].filter((item) => item.enabled),
    [
      showRsi,
      showSma,
      showEma,
      showWma,
      showWema,
      showTypicalPrice,
      showBollingerBands,
      showPsar,
      showVolumeProfile,
      showIchimokuCloud,
      showVwap,
      getPlacement,
    ]
  );

  const getOverlayValueAtTime = useCallback(
    (id: IndicatorId, time: number): number | null => {
      const pickLine = (arr: Array<{ time: UTCTimestamp; value?: number }>) => {
        const hit = arr.find((d) => Number(d.time) === time && typeof (d as any).value === 'number');
        return hit && typeof (hit as any).value === 'number' ? Number((hit as any).value) : null;
      };

      switch (id) {
        case 'sma':
          return pickLine(smaData as any);
        case 'ema':
          return pickLine(emaData as any);
        case 'wma':
          return pickLine(wmaData as any);
        case 'wema':
          return pickLine(wemaData as any);
        case 'typicalPrice':
          return pickLine(typicalPriceData as any);
        case 'bollingerBands': {
          const hit = bollingerData.middle.find((d) => Number(d.time) === time);
          return hit && typeof hit.value === 'number' ? Number(hit.value) : null;
        }
        case 'rsi':
          return pickLine(rsiData as any);
        case 'vwap':
          return pickLine(vwapData as any);
        case 'psar':
          return pickLine(psarData as any);
        case 'ichimokuCloud': {
          const hit = ichimokuConversionData.find((d) => Number(d.time) === time);
          return hit && typeof hit.value === 'number' ? Number(hit.value) : null;
        }
        case 'volumeProfile':
          return null; // rendered as histogram; skip numeric label for now
        default:
          return null;
      }
    },
    [
      bollingerData.middle,
      emaData,
      ichimokuConversionData,
      psarData,
      smaData,
      rsiData,
      typicalPriceData,
      vwapData,
      wemaData,
      wmaData,
    ]
  );

  const getOverlaySeries = useCallback(
    (id: IndicatorId) => {
      const entry = Object.values(overlaySeriesRef.current).find((s) => s.baseId === id);
      return entry?.series ?? null;
    },
    []
  );

  const buildPlacementMenu = useCallback(
    (indicatorId: IndicatorId, inPane: boolean): MenuProps => ({
      onClick: ({ key }) => {
        if (key === 'main' || key === 'pane') {
          onPlacementChange?.(indicatorId, key as IndicatorPlacement);
        }
      },
      items: [
        {
          key: 'main',
          label: 'Move to main chart',
          disabled: !inPane,
        },
        {
          key: 'pane',
          label: 'Move to new pane',
          disabled: inPane,
        },
        { type: 'divider' },
        {
          key: 'up',
          label: 'Move pane up (coming soon)',
          disabled: true,
        },
        {
          key: 'down',
          label: 'Move pane down (coming soon)',
          disabled: true,
        },
      ],
    }),
    [onPlacementChange]
  );

  const ensureOverlayPaneChart = useCallback(
    (indicatorId: IndicatorId) => {
      if (overlayPaneCharts.current[indicatorId]) return overlayPaneCharts.current[indicatorId];
      const container = overlayPaneRefs.current[indicatorId];
      if (!container) return null;
      const chart = createChart(container, {
      width: getActualWidth(),
      height: subHeight,
      layout: {
        background: { color: theme.chartBackground },
        textColor: theme.textSecondary,
        },
        grid: {
          horzLines: { color: theme.chartGrid },
          vertLines: { color: theme.chartGrid },
        },
      rightPriceScale: {
        borderColor: theme.chartPriceScaleBorder,
      },
      timeScale: {
        barSpacing: 12,
        minBarSpacing: 8,
        timeVisible: true,
        borderColor: theme.chartPriceScaleBorder,
      },
    });
      overlayPaneCharts.current[indicatorId] = chart;
      setOverlayPaneVersion((v) => v + 1);

      // crosshair sync from pane to main
      const handler = (param: any) => {
        if (param?.point) {
          if (typeof param.logical === 'number') {
            setSharedCrosshairLogical(param.logical);
          } else {
            const logical = chart.timeScale().coordinateToLogical(param.point.x);
            if (typeof logical === 'number') {
              setSharedCrosshairLogical(logical);
            }
          }
          const time = chart.timeScale().coordinateToTime(param.point.x) as UTCTimestamp | undefined;
          setSharedCrosshairTime(typeof time === 'number' ? time : null);
        }
      };
      chart.subscribeCrosshairMove(handler);
      overlayPaneCrosshairHandlers.current[indicatorId] = handler;

      return chart;
    },
    [getActualWidth, subHeight, theme]
  );

  useEffect(() => {
    const paneIds: IndicatorId[] = overlayLegend
      .filter((item) => item.placement === 'pane')
      .map((item) => item.id as IndicatorId);

    Object.keys(overlayPaneCharts.current).forEach((key) => {
      const id = key as IndicatorId;
      if (!paneIds.includes(id)) {
        overlayPaneCharts.current[id]?.remove();
        if (overlayPaneCrosshairHandlers.current[id]) {
          overlayPaneCharts.current[id]?.unsubscribeCrosshairMove?.(
            overlayPaneCrosshairHandlers.current[id]!
          );
          delete overlayPaneCrosshairHandlers.current[id];
        }
        delete overlayPaneCharts.current[id];
        setOverlayPaneVersion((v) => v + 1);
      }
    });
    // clean overlay series that belong to removed pane charts
    Object.entries(overlaySeriesRef.current).forEach(([key, entry]) => {
      if (entry.target === 'overlayPane' && !paneIds.includes(entry.baseId)) {
        teardownOverlay(key);
      }
    });
  }, [overlayLegend, teardownOverlay]);

  // After creating/removing overlay panes, snap their visible range to main chart
  useEffect(() => {
    const paneIds: IndicatorId[] = overlayLegend
      .filter((item) => item.placement === 'pane')
      .map((item) => item.id as IndicatorId);
    if (!mainChart.current || paneIds.length === 0) return;
    const range = mainChart.current.timeScale().getVisibleRange();
    if (!range || typeof range.from !== 'number' || typeof range.to !== 'number') return;
    paneIds.forEach((id) => {
      const scale = overlayPaneCharts.current[id]?.timeScale();
      try {
        scale?.setVisibleRange(range);
      } catch (e) {
        // ignore if scale not ready yet
      }
    });
  }, [overlayPaneVersion, overlayLegend]);

  // Live sync visible time range between main chart and overlay panes
  useEffect(() => {
    const paneIds: IndicatorId[] = overlayLegend
      .filter((item) => item.placement === 'pane')
      .map((item) => item.id as IndicatorId);
    if (!mainChart.current || paneIds.length === 0) return;

    const mainScale = mainChart.current.timeScale();
    const overlayScales = paneIds
      .map((id) => overlayPaneCharts.current[id]?.timeScale())
      .filter((scale): scale is ReturnType<IChartApi['timeScale']> => Boolean(scale));

    if (overlayScales.length === 0) return;

    const isValidRange = (r: any) =>
      r && typeof r.from === 'number' && Number.isFinite(r.from) && typeof r.to === 'number' && Number.isFinite(r.to);

    const syncFromMain = (range: any, force = false) => {
      if (!isValidRange(range) || overlayPaneTimeSyncGuardRef.current) return;
      // honor user detaching from right edge: don't force snap if user has scrolled away,
      // unless this is an explicit initial snap (force=true)
      if (!force && userDetachedFromRightRef.current) return;
      overlayPaneTimeSyncGuardRef.current = true;
      overlayScales.forEach((s) => {
        try {
          s.setVisibleRange(range);
        } catch {}
      });
      overlayPaneTimeSyncGuardRef.current = false;
    };

    // initial snap for new/changed overlay panes
    const initialRange = mainScale.getVisibleRange();
    if (initialRange) {
      syncFromMain(initialRange, true);
    }

    mainScale.subscribeVisibleTimeRangeChange(syncFromMain);

    return () => {
      mainScale.unsubscribeVisibleTimeRangeChange(syncFromMain);
    };
  }, [overlayLegend, overlayPaneVersion]);

  // Sync native indicator panes (volume/oscillators) to main time range and vice versa
  useEffect(() => {
    if (!mainChart.current) return;

    // When indicator charts mount/unmount, resync them once without disturbing user scroll thereafter
    const indicatorScales = [
      volumeChart.current?.timeScale(),
      macdChart.current?.timeScale(),
      obvChart.current?.timeScale(),
      williamsChart.current?.timeScale(),
      atrChart.current?.timeScale(),
      mfiChart.current?.timeScale(),
      stochasticChart.current?.timeScale(),
      rocChart.current?.timeScale(),
      trixChart.current?.timeScale(),
      adlChart.current?.timeScale(),
      adxChart.current?.timeScale(),
      aoChart.current?.timeScale(),
      cciChart.current?.timeScale(),
      fiChart.current?.timeScale(),
      kstChart.current?.timeScale(),
      stochRsiChart.current?.timeScale(),
    ].filter((scale): scale is ReturnType<IChartApi['timeScale']> => Boolean(scale));

    if (indicatorScales.length === 0) return;

    const mainScale = mainChart.current.timeScale();
    const isValidRange = (r: any) =>
      r && typeof r.from === 'number' && Number.isFinite(r.from) && typeof r.to === 'number' && Number.isFinite(r.to);

    const syncRangeFromMain = (range: any) => {
      if (!isValidRange(range) || indicatorPaneTimeSyncGuardRef.current) return;
      indicatorPaneTimeSyncGuardRef.current = true;
      indicatorScales.forEach((s) => {
        try {
          s.setVisibleRange(range);
        } catch {}
      });
      indicatorPaneTimeSyncGuardRef.current = false;
    };

    // Initial snap to main chart time range
    const initialRange = mainScale.getVisibleRange();
    if (initialRange) {
      syncRangeFromMain(initialRange);
    }

    mainScale.subscribeVisibleTimeRangeChange(syncRangeFromMain);

    const disposers: Array<() => void> = [];
    indicatorScales.forEach((scale) => {
      const handler = (range: any) => {
        if (!isValidRange(range) || indicatorPaneTimeSyncGuardRef.current) return;
        indicatorPaneTimeSyncGuardRef.current = true;
        try {
          mainScale.setVisibleRange(range);
        } catch {
          // ignore disposed chart
        }
        indicatorPaneTimeSyncGuardRef.current = false;
      };
      scale.subscribeVisibleTimeRangeChange(handler);
      disposers.push(() => {
        try {
          scale.unsubscribeVisibleTimeRangeChange(handler);
        } catch {
          // ignore disposed chart
        }
      });
    });

    return () => {
      mainScale.unsubscribeVisibleTimeRangeChange(syncRangeFromMain);
      disposers.forEach((dispose) => dispose());
    };
  }, [
    mainChart.current,
    showVolume,
    showMacd,
    showObv,
    showWilliamsR,
    showAtr,
    showMfi,
    showStochastic,
    showRoc,
    showTrix,
    showAdl,
    showAdx,
    showAo,
    showCci,
    showForceIndex,
    showKst,
    showStochRsi,
  ]);

  // Unify time-scale appearance: hide axes for all panes except the bottom-most chart
  useEffect(() => {
    const chartsInOrder: Array<IChartApi | null> = [
      mainChart.current,
      volumeChart.current,
      macdChart.current,
      obvChart.current,
      williamsChart.current,
      atrChart.current,
      mfiChart.current,
      stochasticChart.current,
      rocChart.current,
      trixChart.current,
      adlChart.current,
      adxChart.current,
      aoChart.current,
      cciChart.current,
      fiChart.current,
      kstChart.current,
      stochRsiChart.current,
      // overlay pane charts (preserve overlayLegend order)
      ...overlayLegend
        .filter((item) => item.placement === 'pane')
        .map((item) => overlayPaneCharts.current[item.id as IndicatorId] ?? null),
    ];
    const existing = chartsInOrder.filter(isChartAlive);
    if (!existing.length) return;
    const lastIndex = existing.length - 1;
    existing.forEach((chart, idx) => applyTimeScaleAppearance(chart, idx === lastIndex));
  }, [
    applyTimeScaleAppearance,
    overlayLegend,
    overlayPaneVersion,
    showVolume,
    showMacd,
    showObv,
    showWilliamsR,
    showAtr,
    showMfi,
    showStochastic,
    showRoc,
    showTrix,
    showAdl,
    showAdx,
    showAo,
    showCci,
    showForceIndex,
    showKst,
    showStochRsi,
    showVolumeProfile,
  ]);

  // Force all panes to share exactly the same visible logical range as main (initial snap + whenever data changes)
  const initialRangeSyncedRef = useRef(false);
  useEffect(() => {
    if (initialRangeSyncedRef.current) return;
    const syncToMainRange = () => {
      if (!mainChart.current) return;
      const mainScale = mainChart.current.timeScale();
      const range = mainScale.getVisibleRange();
      if (!range) return;
      const scales = [
        volumeChart.current?.timeScale(),
        macdChart.current?.timeScale(),
        obvChart.current?.timeScale(),
        williamsChart.current?.timeScale(),
        atrChart.current?.timeScale(),
        mfiChart.current?.timeScale(),
        stochasticChart.current?.timeScale(),
        rocChart.current?.timeScale(),
        trixChart.current?.timeScale(),
        adlChart.current?.timeScale(),
        adxChart.current?.timeScale(),
        aoChart.current?.timeScale(),
        cciChart.current?.timeScale(),
        fiChart.current?.timeScale(),
        kstChart.current?.timeScale(),
        stochRsiChart.current?.timeScale(),
        ...overlayLegend
          .filter((item) => item.placement === 'pane')
          .map((item) => overlayPaneCharts.current[item.id as IndicatorId]?.timeScale()),
      ]
        .map((s) => {
          try {
            return s;
          } catch {
            return null;
          }
        })
        .filter((s): s is ReturnType<IChartApi['timeScale']> => Boolean(s))
        .filter(isTimeScaleAlive);
      scales.forEach((s) => {
        try {
          s.setVisibleRange(range);
        } catch {}
      });
      initialRangeSyncedRef.current = true;
    };
    syncToMainRange();
  }, [
    ohlcData.length,
    overlayPaneVersion,
  ]);

  useEffect(() => {
    const paneIds: IndicatorId[] = overlayLegend
      .filter((item) => item.placement === 'pane')
      .map((item) => item.id as IndicatorId);

    if (!mainChart.current || paneIds.length === 0) return;

    const mainScale = mainChart.current.timeScale();
    const overlayScales = paneIds
      .map((id) => overlayPaneCharts.current[id]?.timeScale())
      .filter((scale): scale is ReturnType<IChartApi['timeScale']> => Boolean(scale));

    if (overlayScales.length === 0) return;

    const syncFromMain = (range: any) => {
      if (!range || overlayPaneSyncGuardRef.current) return;
      overlayPaneSyncGuardRef.current = true;
      overlayScales.forEach((scale) => scale.setVisibleLogicalRange(range));
      overlayPaneSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
    };
  }, [overlayLegend, overlayPaneVersion]);

  useEffect(() => {
    if (!mainChart.current) return;
    const overlayConfigs: Array<
      [string, boolean, IndicatorId, { color: string; data: any[]; options?: Record<string, any> }]
    > = [
      ['sma', showSma, 'sma', { color: '#13c2c2', data: smaData }],
      ['ema', showEma, 'ema', { color: '#52c41a', data: emaData }],
      ['wma', showWma, 'wma', { color: '#2f54eb', data: wmaData }],
      ['wema', showWema, 'wema', { color: '#fa8c16', data: wemaData }],
      ['rsi', showRsi, 'rsi', { color: '#9c27b0', data: rsiData }],
      ['typicalPrice', showTypicalPrice, 'typicalPrice', { color: '#eb2f96', data: typicalPriceData }],
      ['bbUpper', showBollingerBands, 'bollingerBands', { color: '#722ed1', data: bollingerData.upper }],
      ['bbMiddle', showBollingerBands, 'bollingerBands', { color: '#9254de', data: bollingerData.middle }],
      ['bbLower', showBollingerBands, 'bollingerBands', { color: '#b37feb', data: bollingerData.lower }],
      ['vwap', showVwap, 'vwap', { color: '#d4b106', data: vwapData }],
      [
        'psar',
        showPsar,
        'psar',
        {
          color: '#fa541c',
          data: psarData,
          options: {
            lineVisible: false,
            pointMarkersVisible: true,
            pointMarkersRadius: 2,
            crosshairMarkerVisible: false,
          },
        },
      ],
      ['ichimokuConversion', showIchimokuCloud, 'ichimokuCloud', { color: '#13c2c2', data: ichimokuConversionData }],
      ['ichimokuBase', showIchimokuCloud, 'ichimokuCloud', { color: '#fa541c', data: ichimokuBaseData }],
      ['ichimokuSpanA', showIchimokuCloud, 'ichimokuCloud', { color: '#52c41a', data: ichimokuSpanAData }],
      ['ichimokuSpanB', showIchimokuCloud, 'ichimokuCloud', { color: '#9254de', data: ichimokuSpanBData }],
    ];

    overlayConfigs.forEach(([key, enabled, baseId, cfg]) => {
      if (!enabled || !cfg.data.length) {
        teardownOverlay(key);
        return;
      }
      const targetPlacement = getPlacement(baseId) === 'pane' ? 'overlayPane' : 'main';
      const chart =
        targetPlacement === 'overlayPane' ? ensureOverlayPaneChart(baseId) : mainChart.current;
      if (!chart) {
        teardownOverlay(key);
        return;
      }

      const existing = overlaySeriesRef.current[key];
      if (existing && existing.target !== targetPlacement) {
        teardownOverlay(key);
      }

      // Give RSI its own price scale on the left when overlaid on the main chart to avoid compression
      const priceScaleId =
        targetPlacement === 'main' && baseId === 'rsi' ? 'left' : undefined;

      if (!overlaySeriesRef.current[key]) {
        overlaySeriesRef.current[key] = {
          series: chart.addLineSeries({
          color: cfg.color,
          lineWidth: 2,
          priceLineVisible: baseId === 'rsi' ? true : false,
          lastValueVisible: baseId === 'rsi' ? true : true,
          crosshairMarkerVisible: true,
          priceScaleId,
          ...(cfg.options ?? {}),
        }),
          target: targetPlacement,
          baseId,
        };
      }
      // Configure custom scale for RSI when on the main chart
      if (priceScaleId) {
        chart.priceScale(priceScaleId).applyOptions({
          autoScale: true,
          scaleMargins: { top: 0.1, bottom: 0.1 },
          borderColor: theme.chartPriceScaleBorder,
        });
        // Ensure left axis is visible to show the RSI scale
        chart.applyOptions({
          leftPriceScale: {
            visible: true,
            borderColor: theme.chartPriceScaleBorder,
            scaleMargins: { top: 0.1, bottom: 0.1 },
          },
        });
      }
      overlaySeriesRef.current[key].series.setData(cfg.data);
    });

    return () => {
      overlayConfigs.forEach(([key]) => teardownOverlay(key));
    };
  }, [
    showRsi,
    showSma,
    showEma,
    showWma,
    showWema,
    rsiData,
    showTypicalPrice,
    showBollingerBands,
    showVwap,
    showPsar,
    showIchimokuCloud,
    smaData,
    emaData,
    wmaData,
    wemaData,
    typicalPriceData,
    bollingerData.upper,
    bollingerData.middle,
    bollingerData.lower,
    rsiData,
    vwapData,
    psarData,
    ichimokuConversionData,
    ichimokuBaseData,
    ichimokuSpanAData,
    ichimokuSpanBData,
    getPlacement,
    ensureOverlayPaneChart,
  ]);



  useEffect(() => {
    if (!showMacd) {
      macdHistogramSeriesRef.current?.setData([]);
      macdLineSeriesRef.current?.setData([]);
      macdSignalSeriesRef.current?.setData([]);
      return;
    }

    if (!macdChart.current || !macdHistogramSeriesRef.current || !macdLineSeriesRef.current || !macdSignalSeriesRef.current) {
      return;
    }

    const histogramData = macdData.map((point) => ({
      time: point.time,
      value: point.histogram ?? 0,
      color: (point.histogram ?? 0) >= 0 ? theme.candleUp : theme.candleDown,
    }));

    const macdLineData = macdData.map((point) =>
      point.macd !== undefined ? { time: point.time, value: point.macd } : { time: point.time }
    );

    const signalLineData = macdData.map((point) =>
      point.signal !== undefined ? { time: point.time, value: point.signal } : { time: point.time }
    );

    try {
      macdHistogramSeriesRef.current.setData(histogramData);
      macdLineSeriesRef.current.setData(macdLineData as (LineData<UTCTimestamp> | WhitespaceData<UTCTimestamp>)[]);
      macdSignalSeriesRef.current.setData(signalLineData as (LineData<UTCTimestamp> | WhitespaceData<UTCTimestamp>)[]);
      scrollToRealTimeIfLive(macdChart.current);
    } catch (error) {
      logger.error('Failed to render MACD data', error);
    }
  }, [showMacd, macdData, theme]);

  useEffect(() => {
    if (!showMacd || !macdChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const macdScale = macdChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (macdSyncGuardRef.current || !range) return;
      macdSyncGuardRef.current = true;
      macdScale.setVisibleLogicalRange(range);
      macdSyncGuardRef.current = false;
    };

    const syncFromMacd = (range: any) => {
      if (macdSyncGuardRef.current || !range) return;
      macdSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      macdSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    macdScale.subscribeVisibleLogicalRangeChange(syncFromMacd);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      macdScale.unsubscribeVisibleLogicalRangeChange(syncFromMacd);
    };
  }, [showMacd]);

  useEffect(() => {
    if (!showWilliamsR || !williamsChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const williamsScale = williamsChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (williamsSyncGuardRef.current || !range) return;
      williamsSyncGuardRef.current = true;
      williamsScale.setVisibleLogicalRange(range);
      williamsSyncGuardRef.current = false;
    };

    const syncFromWilliams = (range: any) => {
      if (williamsSyncGuardRef.current || !range) return;
      williamsSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      williamsSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    williamsScale.subscribeVisibleLogicalRangeChange(syncFromWilliams);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      williamsScale.unsubscribeVisibleLogicalRangeChange(syncFromWilliams);
    };
  }, [showWilliamsR]);

  useEffect(() => {
    if (!showObv || !obvChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const obvScale = obvChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (obvSyncGuardRef.current || !range) return;
      obvSyncGuardRef.current = true;
      try {
        obvScale.setVisibleLogicalRange(range);
      } catch {
        // ignore disposed chart
      }
      obvSyncGuardRef.current = false;
    };

    const syncFromObv = (range: any) => {
      if (obvSyncGuardRef.current || !range) return;
      obvSyncGuardRef.current = true;
      try {
        mainScale.setVisibleLogicalRange(range);
      } catch {
        // ignore disposed chart
      }
      obvSyncGuardRef.current = false;
    };

    syncFromMain(mainScale.getVisibleLogicalRange());
    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    obvScale.subscribeVisibleLogicalRangeChange(syncFromObv);

    return () => {
      try {
        mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      } catch {}
      try {
        obvScale.unsubscribeVisibleLogicalRangeChange(syncFromObv);
      } catch {}
    };
  }, [showObv]);

  useEffect(() => {
    if (!showAtr || !atrChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const atrScale = atrChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (atrSyncGuardRef.current || !range) return;
      atrSyncGuardRef.current = true;
      atrScale.setVisibleLogicalRange(range);
      atrSyncGuardRef.current = false;
    };

    const syncFromAtr = (range: any) => {
      if (atrSyncGuardRef.current || !range) return;
      atrSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      atrSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    atrScale.subscribeVisibleLogicalRangeChange(syncFromAtr);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      atrScale.unsubscribeVisibleLogicalRangeChange(syncFromAtr);
    };
  }, [showAtr]);

  useEffect(() => {
    if (!showMfi || !mfiChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const mfiScale = mfiChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (mfiSyncGuardRef.current || !range) return;
      mfiSyncGuardRef.current = true;
      mfiScale.setVisibleLogicalRange(range);
      mfiSyncGuardRef.current = false;
    };

    const syncFromMfi = (range: any) => {
      if (mfiSyncGuardRef.current || !range) return;
      mfiSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      mfiSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    mfiScale.subscribeVisibleLogicalRangeChange(syncFromMfi);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      mfiScale.unsubscribeVisibleLogicalRangeChange(syncFromMfi);
    };
  }, [showMfi]);

  useEffect(() => {
    if (!showStochRsi || !stochRsiChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const stochRsiScale = stochRsiChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (stochRsiSyncGuardRef.current || !range) return;
      stochRsiSyncGuardRef.current = true;
      stochRsiScale.setVisibleLogicalRange(range);
      stochRsiSyncGuardRef.current = false;
    };

    const syncFromStochRsi = (range: any) => {
      if (stochRsiSyncGuardRef.current || !range) return;
      stochRsiSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      stochRsiSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    stochRsiScale.subscribeVisibleLogicalRangeChange(syncFromStochRsi);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      stochRsiScale.unsubscribeVisibleLogicalRangeChange(syncFromStochRsi);
    };
  }, [showStochRsi]);

  useEffect(() => {
    if (!showRoc || !rocChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const rocScale = rocChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (rocSyncGuardRef.current || !range) return;
      rocSyncGuardRef.current = true;
      try {
        rocScale.setVisibleLogicalRange(range);
      } catch {}
      rocSyncGuardRef.current = false;
    };

    const syncFromRoc = (range: any) => {
      if (rocSyncGuardRef.current || !range) return;
      rocSyncGuardRef.current = true;
      try {
        mainScale.setVisibleLogicalRange(range);
      } catch {}
      rocSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    rocScale.subscribeVisibleLogicalRangeChange(syncFromRoc);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      rocScale.unsubscribeVisibleLogicalRangeChange(syncFromRoc);
    };
  }, [showRoc]);

  useEffect(() => {
    if (!showTrix || !trixChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const trixScale = trixChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (trixSyncGuardRef.current || !range) return;
      trixSyncGuardRef.current = true;
      trixScale.setVisibleLogicalRange(range);
      trixSyncGuardRef.current = false;
    };

    const syncFromTrix = (range: any) => {
      if (trixSyncGuardRef.current || !range) return;
      trixSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      trixSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    trixScale.subscribeVisibleLogicalRangeChange(syncFromTrix);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      trixScale.unsubscribeVisibleLogicalRangeChange(syncFromTrix);
    };
  }, [showTrix]);

  useEffect(() => {
    if (!showAdl || !adlChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const adlScale = adlChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (adlSyncGuardRef.current || !range) return;
      adlSyncGuardRef.current = true;
      adlScale.setVisibleLogicalRange(range);
      adlSyncGuardRef.current = false;
    };

    const syncFromAdl = (range: any) => {
      if (adlSyncGuardRef.current || !range) return;
      adlSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      adlSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    adlScale.subscribeVisibleLogicalRangeChange(syncFromAdl);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      adlScale.unsubscribeVisibleLogicalRangeChange(syncFromAdl);
    };
  }, [showAdl]);

  useEffect(() => {
    if (!showForceIndex || !fiChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const fiScale = fiChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (fiSyncGuardRef.current || !range) return;
      fiSyncGuardRef.current = true;
      fiScale.setVisibleLogicalRange(range);
      fiSyncGuardRef.current = false;
    };

    const syncFromFi = (range: any) => {
      if (fiSyncGuardRef.current || !range) return;
      fiSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      fiSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    fiScale.subscribeVisibleLogicalRangeChange(syncFromFi);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      fiScale.unsubscribeVisibleLogicalRangeChange(syncFromFi);
    };
  }, [showForceIndex]);

  useEffect(() => {
    if (!showKst || !kstChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const kstScale = kstChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (kstSyncGuardRef.current || !range) return;
      kstSyncGuardRef.current = true;
      kstScale.setVisibleLogicalRange(range);
      kstSyncGuardRef.current = false;
    };

    const syncFromKst = (range: any) => {
      if (kstSyncGuardRef.current || !range) return;
      kstSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      kstSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    kstScale.subscribeVisibleLogicalRangeChange(syncFromKst);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      kstScale.unsubscribeVisibleLogicalRangeChange(syncFromKst);
    };
  }, [showKst]);

  useEffect(() => {
    if (!showAdx || !adxChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const adxScale = adxChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (adxSyncGuardRef.current || !range) return;
      adxSyncGuardRef.current = true;
      adxScale.setVisibleLogicalRange(range);
      adxSyncGuardRef.current = false;
    };

    const syncFromAdx = (range: any) => {
      if (adxSyncGuardRef.current || !range) return;
      adxSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      adxSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    adxScale.subscribeVisibleLogicalRangeChange(syncFromAdx);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      adxScale.unsubscribeVisibleLogicalRangeChange(syncFromAdx);
    };
  }, [showAdx]);

  useEffect(() => {
    if (!showAo || !aoChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const aoScale = aoChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (aoSyncGuardRef.current || !range) return;
      aoSyncGuardRef.current = true;
      aoScale.setVisibleLogicalRange(range);
      aoSyncGuardRef.current = false;
    };

    const syncFromAo = (range: any) => {
      if (aoSyncGuardRef.current || !range) return;
      aoSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      aoSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    aoScale.subscribeVisibleLogicalRangeChange(syncFromAo);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      aoScale.unsubscribeVisibleLogicalRangeChange(syncFromAo);
    };
  }, [showAo]);

  useEffect(() => {
    if (!showCci || !cciChart.current || !mainChart.current) return;

    const mainScale = mainChart.current.timeScale();
    const cciScale = cciChart.current.timeScale();

    const syncFromMain = (range: any) => {
      if (cciSyncGuardRef.current || !range) return;
      cciSyncGuardRef.current = true;
      cciScale.setVisibleLogicalRange(range);
      cciSyncGuardRef.current = false;
    };

    const syncFromCci = (range: any) => {
      if (cciSyncGuardRef.current || !range) return;
      cciSyncGuardRef.current = true;
      mainScale.setVisibleLogicalRange(range);
      cciSyncGuardRef.current = false;
    };

    mainScale.subscribeVisibleLogicalRangeChange(syncFromMain);
    cciScale.subscribeVisibleLogicalRangeChange(syncFromCci);

    return () => {
      mainScale.unsubscribeVisibleLogicalRangeChange(syncFromMain);
      cciScale.unsubscribeVisibleLogicalRangeChange(syncFromCci);
    };
  }, [showCci]);

  useEffect(() => {
    if (!mainChart.current || !candleSeriesRef.current) return;

    type CrosshairTarget = {
      key: string;
      chart: IChartApi;
      series: ISeriesApi<any>;
      getValue: (time: number) => number | undefined;
    };

    const getTimeNumber = (time: any): number | undefined => {
      if (typeof time === 'number') return time;
      if (time && typeof time === 'object' && 'timestamp' in time) {
        return (time as { timestamp: number }).timestamp;
      }
      return undefined;
    };

    const disposers: Array<() => void> = [];
    let isCrosshairSyncing = false;

    const attachSync = (source: CrosshairTarget, targets: CrosshairTarget[]) => {
      const sourceTimeScale = getSafeTimeScale(source.chart);
      if (!source.chart || !source.series || !sourceTimeScale || targets.length === 0) return;
      const handler = (param: any) => {
        if (isCrosshairSyncing) return;
        const liveSourceScale = getSafeTimeScale(source.chart);
        if (!liveSourceScale) return;
        const timeValue = getTimeNumber(param.time);
        if (!param.point) {
          targets.forEach((target) => {
            try {
              if (isChartAlive(target.chart)) {
                target.chart.clearCrosshairPosition();
              }
            } catch {
              // ignore disposed targets
            }
          });
          return;
        }

        isCrosshairSyncing = true;
        try {
          const coordinate =
            param.time !== undefined ? liveSourceScale.timeToCoordinate(param.time) : null;

          targets.forEach(({ chart, series, getValue }) => {
            const targetScale = getSafeTimeScale(chart);
            if (
              !targetScale ||
              !isChartAlive(chart) ||
              !series ||
              timeValue === undefined ||
              coordinate === null ||
              coordinate === undefined
            ) {
              try {
                chart?.clearCrosshairPosition();
              } catch {
                // ignore disposed targets
              }
              return;
            }

            const targetTime = targetScale.coordinateToTime(coordinate as number);
            if (targetTime === undefined || targetTime === null) {
              try {
                chart.clearCrosshairPosition();
              } catch {
                // ignore disposed targets
              }
              return;
            }

            const value = getValue(timeValue);
            if (value === undefined) {
              try {
                chart.clearCrosshairPosition();
              } catch {
                // ignore disposed targets
              }
              return;
            }

            try {
              chart.setCrosshairPosition(value, targetTime as any, series);
            } catch (err) {
              logger.warn('Skipped crosshair sync on disposed chart', err);
              try {
                chart.clearCrosshairPosition();
              } catch {
                // ignore disposed targets
              }
            }
          });
        } finally {
          isCrosshairSyncing = false;
        }
      };

      source.chart.subscribeCrosshairMove(handler);
      disposers.push(() => {
        try {
          source.chart.unsubscribeCrosshairMove(handler);
        } catch {
          // ignore disposed charts during cleanup
        }
      });
    };

    const getPriceAt = (time: number) => ohlcByTime.get(time)?.close;
    const getVolumeAt = (time: number) => ohlcByTime.get(time)?.volume;
    const getMacdAt = (time: number) => macdByTime.get(time);
    const getObvAt = (time: number) => obvData.find((d) => Number(d.time) === time)?.value;
    const getWilliamsAt = (time: number) => williamsByTime.get(time);
    const getAtrAt = (time: number) => atrByTime.get(time);
    const getMfiAt = (time: number) => mfiByTime.get(time);
    const getStochasticAt = (time: number) => stochasticKByTime.get(time);
    const getStochRsiAt = (time: number) => stochRsiKByTime.get(time);
    const getRocAt = (time: number) => rocByTime.get(time);
    const getTrixAt = (time: number) => trixByTime.get(time);
    const getAdlAt = (time: number) => adlByTime.get(time);
    const getFiAt = (time: number) => fiByTime.get(time);
    const getKstAt = (time: number) => kstByTime.get(time);
    const getAdxAt = (time: number) => adxData.find((d) => Number(d.time) === time)?.adx;
    const getAoAt = (time: number) => aoData.find((d) => Number(d.time) === time)?.value;
    const getCciAt = (time: number) => cciByTime.get(time);

    const targets: Array<CrosshairTarget | null> = [
      {
        key: 'price',
        chart: mainChart.current,
        series: candleSeriesRef.current!,
        getValue: getPriceAt,
      },
      showVolume && volumeChart.current && volumeSeriesRef.current
        ? {
            key: 'volume',
            chart: volumeChart.current,
            series: volumeSeriesRef.current,
            getValue: getVolumeAt,
          }
        : null,
      showMacd &&
      macdChart.current &&
      ((macdLineSeriesRef.current as ISeriesApi<'Line'> | null) ||
        (macdHistogramSeriesRef.current as ISeriesApi<'Histogram'> | null))
        ? {
            key: 'macd',
            chart: macdChart.current,
            series:
              (macdLineSeriesRef.current as ISeriesApi<'Line'>) ||
              (macdHistogramSeriesRef.current as ISeriesApi<'Histogram'>),
            getValue: getMacdAt,
          }
        : null,
      showObv && obvChart.current && obvSeriesRef.current
        ? {
            key: 'obv',
            chart: obvChart.current,
            series: obvSeriesRef.current,
            getValue: getObvAt,
          }
        : null,
      showWilliamsR && williamsChart.current && williamsSeriesRef.current
        ? {
            key: 'williams',
            chart: williamsChart.current,
            series: williamsSeriesRef.current,
            getValue: getWilliamsAt,
          }
        : null,
      showAtr && atrChart.current && atrSeriesRef.current
        ? {
            key: 'atr',
            chart: atrChart.current,
            series: atrSeriesRef.current,
            getValue: getAtrAt,
          }
        : null,
      showMfi && mfiChart.current && mfiSeriesRef.current
        ? {
            key: 'mfi',
            chart: mfiChart.current,
            series: mfiSeriesRef.current,
            getValue: getMfiAt,
          }
        : null,
      showStochastic &&
      stochasticChart.current &&
      (stochasticKSeriesRef.current || stochasticDSeriesRef.current)
        ? {
            key: 'stochastic',
            chart: stochasticChart.current,
            series:
              (stochasticKSeriesRef.current as ISeriesApi<'Line'>) ||
              (stochasticDSeriesRef.current as ISeriesApi<'Line'>),
            getValue: getStochasticAt,
          }
        : null,
      showStochRsi &&
      stochRsiChart.current &&
      (stochRsiKSeriesRef.current || stochRsiDSeriesRef.current)
        ? {
            key: 'stochRsi',
            chart: stochRsiChart.current,
            series:
              (stochRsiKSeriesRef.current as ISeriesApi<'Line'>) ||
              (stochRsiDSeriesRef.current as ISeriesApi<'Line'>),
            getValue: getStochRsiAt,
          }
        : null,
      showRoc && rocChart.current && rocSeriesRef.current
        ? {
            key: 'roc',
            chart: rocChart.current,
            series: rocSeriesRef.current,
            getValue: getRocAt,
          }
        : null,
      showTrix && trixChart.current && trixSeriesRef.current
        ? {
            key: 'trix',
            chart: trixChart.current,
            series: trixSeriesRef.current,
            getValue: getTrixAt,
          }
        : null,
      showAdl && adlChart.current && adlSeriesRef.current
        ? {
            key: 'adl',
            chart: adlChart.current,
            series: adlSeriesRef.current,
            getValue: getAdlAt,
          }
        : null,
      showForceIndex && fiChart.current && fiSeriesRef.current
        ? {
            key: 'forceIndex',
            chart: fiChart.current,
            series: fiSeriesRef.current,
            getValue: getFiAt,
          }
        : null,
      showKst &&
      kstChart.current &&
      (kstSeriesRef.current || kstSignalSeriesRef.current)
        ? {
            key: 'kst',
            chart: kstChart.current,
            series:
              (kstSeriesRef.current as ISeriesApi<'Line'>) ||
              (kstSignalSeriesRef.current as ISeriesApi<'Line'>),
            getValue: getKstAt,
          }
        : null,
      showAdx &&
      adxChart.current &&
      (adxSeriesRef.current || adxPlusDiSeriesRef.current || adxMinusDiSeriesRef.current)
        ? {
            key: 'adx',
            chart: adxChart.current,
            series:
              (adxSeriesRef.current as ISeriesApi<'Line'>) ||
              (adxPlusDiSeriesRef.current as ISeriesApi<'Line'>) ||
              (adxMinusDiSeriesRef.current as ISeriesApi<'Line'>),
            getValue: getAdxAt,
          }
        : null,
      showAo && aoChart.current && aoSeriesRef.current
        ? {
            key: 'ao',
            chart: aoChart.current,
            series: aoSeriesRef.current,
            getValue: getAoAt,
          }
        : null,
      showCci && cciChart.current && cciSeriesRef.current
        ? {
            key: 'cci',
            chart: cciChart.current,
            series: cciSeriesRef.current,
            getValue: getCciAt,
          }
        : null,
    ];

    const activeTargets = targets.filter(Boolean) as CrosshairTarget[];
    activeTargets.forEach((source, idx) => {
      const others = activeTargets.filter((_, i) => i !== idx);
      attachSync(source, others);
    });

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }, [
    showVolume,
    showMacd,
    showObv,
    showWilliamsR,
    showAtr,
    showMfi,
    showStochastic,
    showRoc,
    showTrix,
    showAdl,
    showForceIndex,
    showAdx,
    showAo,
    showCci,
    ohlcByTime,
    macdByTime,
    obvData,
    williamsByTime,
    atrByTime,
    mfiByTime,
    stochasticKByTime,
    stochRsiKByTime,
    rocByTime,
    trixByTime,
    adlByTime,
    fiByTime,
    kstByTime,
    adxData,
    aoData,
    cciByTime,
  ]);

  useEffect(() => {
    if (!candleSeriesRef.current || !mainChart.current || ohlcData.length === 0) {
      return;
    }

    const isValidCandle = (candle: any) =>
      Number.isFinite(candle?.time) &&
      Number.isFinite(candle?.open) &&
      Number.isFinite(candle?.high) &&
      Number.isFinite(candle?.low) &&
      Number.isFinite(candle?.close);

    if (lastUpdateType === 'initial' || !isInitialDataSet.current) {
      try {
        candleSeriesRef.current.setData(cleanOhlc);
        if (!isInitialDataSet.current) {
          mainChart.current.timeScale().scrollToRealTime();
          isInitialDataSet.current = true;
        }
      } catch (error) {
        logger.error(`❌ Error setting chart data:`, error);
      }
    } else if (lastUpdateType === 'updateCandle' || lastUpdateType === 'newCandle') {
      if (lastUpdatedCandle) {
        try {
          if (isValidCandle(lastUpdatedCandle)) {
            candleSeriesRef.current.update(lastUpdatedCandle);
          } else {
            // Fallback to full redraw with sanitized data if the incremental candle is invalid
            candleSeriesRef.current.setData(cleanOhlc);
          }
        } catch (error) {
          logger.warn('Incremental update failed, falling back to setData:', error);
          candleSeriesRef.current.setData(cleanOhlc);
        }
      }
    }
  }, [ohlcData, cleanOhlc, lastUpdateType, lastUpdatedCandle]);

  useEffect(() => {
    if (mainChart.current) {
      mainChart.current.applyOptions({
        handleScroll: !activeTool,
        handleScale: !activeTool,
      });
    }
  }, [activeTool]);

  // Adjust time-scale tick formatter based on interval granularity
  useEffect(() => {
    if (!mainChart.current) return;
    const fmt = (tsSec: number, pattern: 'hm' | 'mdh' | 'md') => {
      const d = new Date(tsSec * 1000);
      const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
      switch (pattern) {
        case 'hm':
          return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
        case 'mdh':
          return `${d.toLocaleString(undefined, { month: 'short' })} ${d.getDate()} ${pad(d.getHours())}:00`;
        default:
          return `${d.toLocaleString(undefined, { month: 'short' })} ${d.getDate()}`;
      }
    };

    const tickMarkFormatter = (time: any) => {
      const ts = typeof time === 'number' ? time : (time?.time ?? time);
      if (!ts || isNaN(ts)) return '';
      const i = (window as any)?.selectedTimeframe as string | undefined;
      if (i && i.toLowerCase() === '1m') return fmt(ts, 'hm');
      if (i && i.toLowerCase() === '1h') return fmt(ts, 'mdh');
      return fmt(ts, 'md');
    };

    const applyFormatter = (chart: IChartApi | null | undefined) => {
      chart?.applyOptions({
        timeScale: {
          timeVisible: true,
          tickMarkFormatter,
        },
      });
    };

    const chartsToUpdate: Array<IChartApi | null | undefined> = [
      mainChart.current,
      volumeChart.current,
      macdChart.current,
      obvChart.current,
      williamsChart.current,
      atrChart.current,
      mfiChart.current,
      stochasticChart.current,
      rocChart.current,
      trixChart.current,
      adlChart.current,
      adxChart.current,
      aoChart.current,
      cciChart.current,
      fiChart.current,
      kstChart.current,
      stochRsiChart.current,
    ];

    chartsToUpdate.forEach(applyFormatter);
    Object.values(overlayPaneCharts.current).forEach(applyFormatter);
  }, [
    ohlcData,
    showVolume,
    showMacd,
    showObv,
    showWilliamsR,
    showAtr,
    showMfi,
    showStochastic,
    showRoc,
    showTrix,
    showAdl,
    showAdx,
    showAo,
    showCci,
    showForceIndex,
    showKst,
    showStochRsi,
    overlayPaneVersion,
  ]);


  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {!hideToolbar && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          padding: '12px 16px',
          background: theme.toolbarBackground,
          borderRadius: '8px',
          border: `1px solid ${theme.toolbarBorder}`
        }}>
          <Space size="middle">
            <Tooltip title={rectToolActive ? t('tools.exitRectangleTool') : t('tools.rectangleTool')}>
              <Button
                type={rectToolActive ? "primary" : "default"}
                icon={<BorderOuterOutlined />}
                onClick={handleRectToolToggle}
                size="small"
              >
                {t('tools.rectangleTool')}
              </Button>
            </Tooltip>

            <Tooltip title={pointToolActive ? t('tools.exitPointTool') : t('tools.pointTool')}>
              <Button
                type={pointToolActive ? "primary" : "default"}
                icon={<AimOutlined />}
                onClick={handlePointToolToggle}
                size="small"
              >
                {t('tools.pointTool')}
              </Button>
            </Tooltip>
          </Space>

          <Space size="small">
            {hasSelectedRect && (
              <Tooltip title={t('tools.removeRectangle')}>
                <Button
                  type="text"
                  icon={<ClearOutlined />}
                  onClick={handleClearRect}
                  size="small"
                  danger
                >
                  {t('tools.removeRectangle')}
                </Button>
              </Tooltip>
            )}

            {hasSelectedPoints && (
              <Tooltip title={t('tools.clearPoints')}>
                <Button
                  type="text"
                  icon={<ClearOutlined />}
                  onClick={handleClearPoints}
                  size="small"
                  danger
                >
                  {t('tools.clearPoints')}
                </Button>
              </Tooltip>
            )}
          </Space>
        </div>
      )}

      <div style={{ position: 'relative' }}>
        {/* 短选区提示 - 浮动在图表右上角 */}
        {showShortSelectionHint && (
          <div style={{
            position: 'absolute',
            top: 12,
            right: 60,
            zIndex: 100,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 14px',
            maxWidth: 320,
            background: theme.toolbarBackground,
            border: `1px solid ${theme.toolbarBorder}`,
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <InfoCircleOutlined style={{ color: '#faad14', fontSize: 16, marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: theme.textPrimary, lineHeight: 1.5 }}>
                {t('tools.shortSelectionHint', { count: shortSelectionCount })}
              </div>
              <div 
                style={{ 
                  fontSize: 11, 
                  color: theme.textSecondary, 
                  marginTop: 6,
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => {
                  localStorage.setItem(SHORT_SELECTION_HINT_KEY, 'true');
                  setShowShortSelectionHint(false);
                }}
              >
                {t('tools.dontShowAgain')}
              </div>
            </div>
            <CloseOutlined 
              style={{ 
                cursor: 'pointer', 
                opacity: 0.6, 
                fontSize: 12,
                padding: 2,
                flexShrink: 0
              }}
              onClick={() => setShowShortSelectionHint(false)}
            />
          </div>
        )}

        <RectangleTool
        ref={rectangleToolRef}
        onRectChange={handleRectChange}
        width={getActualWidth()}
        height={mainHeight}
        chartApi={mainChart.current}
        ohlcData={ohlcData}
        onSelectModeChange={handleRectSelectModeChange}
        areaSeries={areaSeriesRef.current}
        hideControls={true}
        externalSelectMode={rectToolActive}
      >
        <PointTool
          ref={pointToolRef}
          onPointsChange={handlePointsChange}
          width={getActualWidth()}
          height={mainHeight}
          chartApi={mainChart.current}
          ohlcData={ohlcData}
          areaSeries={areaSeriesRef.current}
          onSelectModeChange={handlePointSelectModeChange}
          hideControls={true}
          externalSelectMode={pointToolActive}
        >
          <div
            style={{
              width: getActualWidth(),
              height: mainHeight,
              marginBottom: 8,
              border: 'none',
              borderRadius: '6px',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div
              ref={mainChartRef}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
            {sharedCrosshairLogical !== null && mainChart.current && (
              (() => {
                const x = mainChart.current.timeScale().logicalToCoordinate(sharedCrosshairLogical as any);
                if (typeof x !== 'number' || x < 0 || x > getActualWidth()) return null;
                return (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: x,
                      width: 1,
                      background: theme.textSecondary,
                      opacity: 0.4,
                      pointerEvents: 'none',
                      zIndex: 3,
                    }}
                  />
                );
              })()
            )}
            {sharedCrosshairPrice !== null && candleSeriesRef.current && (
              (() => {
                const y = candleSeriesRef.current?.priceToCoordinate(sharedCrosshairPrice);
                if (typeof y !== 'number') return null;
                return (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: y,
                      height: 1,
                      background: theme.textSecondary,
                      opacity: 0.4,
                      pointerEvents: 'none',
                      zIndex: 3,
                    }}
                  />
                );
              })()
            )}
            {showIchimokuCloud && ichimokuCloudSegments.length > 0 && (
              <svg
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                {ichimokuCloudSegments.map((segment, idx) => (
                  <path
                    key={`ichimoku-${idx}`}
                    d={segment.path}
                    fill={segment.bullish ? 'rgba(82, 196, 26, 0.18)' : 'rgba(245, 34, 45, 0.18)'}
                    stroke="none"
                  />
                ))}
              </svg>
            )}
            {sharedCrosshairTime !== null && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 6,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: theme.surfaceBackground,
                  color: theme.textSecondary,
                  border: `1px solid ${theme.surfaceBorder}`,
                  borderRadius: 6,
                  padding: '2px 8px',
                  fontSize: 11,
                  zIndex: 4,
                  pointerEvents: 'none',
                }}
              >
                {formatTimestampToEST(sharedCrosshairTime)}
              </div>
            )}
            {showVolumeProfile && volumeProfileBars.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: Math.max(8, priceScaleWidthRef.current + 8),
                  width: volumeProfileWidth,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                {volumeProfileBars.map((bar, idx) => {
                  const fill = bar.isPoc
                    ? 'rgba(147, 78, 200, 0.9)'
                    : bar.isValueArea
                    ? 'rgba(177, 119, 216, 0.6)'
                    : 'rgba(177, 119, 216, 0.35)';
                  const border = bar.isPoc
                    ? '1px solid rgba(147, 78, 200, 1)'
                    : '1px solid rgba(177, 119, 216, 0.6)';
                  return (
                    <div
                      key={`vp-${idx}`}
                      style={{
                        position: 'absolute',
                        top: bar.top,
                        height: bar.height,
                        right: 0,
                        width: bar.width,
                        borderRadius: 2,
                        background: fill,
                        border,
                      }}
                    />
                  );
                })}
                {typeof volumeProfileLineCoords.vahY === 'number' && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: volumeProfileLineCoords.vahY,
                      borderTop: '1px dashed rgba(177, 119, 216, 0.9)',
                    }}
                  />
                )}
                {typeof volumeProfileLineCoords.valY === 'number' && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: volumeProfileLineCoords.valY,
                      borderTop: '1px dashed rgba(177, 119, 216, 0.9)',
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </PointTool>
      </RectangleTool>
      </div>

      {/* Overlay legend */}
      {overlayLegend.filter((item) => item.placement === 'main').length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            zIndex: 5,
          }}
          ref={overlayLegendRef}
        >
          {overlayLegend
            .filter((item) => item.placement === 'main')
            .map((item) => {
            const inPane = item.placement === 'pane';

            const volumeProfileSettings =
              item.id === 'volumeProfile' && showVolumeProfile ? (
                <Popover
                  trigger="click"
                  placement="bottomLeft"
                  content={
                    <div style={{ display: 'grid', gap: 8, minWidth: 220 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: theme.textSecondary }}>Price Buckets</span>
                        <InputNumber
                          size="small"
                          min={10}
                          max={100}
                          value={volumeProfileBuckets}
                          onChange={(value) =>
                            setVolumeProfileBuckets(
                              clampNumber(value, 10, 100, volumeProfileBuckets)
                            )
                          }
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: theme.textSecondary }}>Width %</span>
                        <InputNumber
                          size="small"
                          min={0.1}
                          max={0.5}
                          step={0.05}
                          value={volumeProfileWidthPct}
                          onChange={(value) =>
                            setVolumeProfileWidthPct(
                              clampNumber(value, 0.1, 0.5, volumeProfileWidthPct)
                            )
                          }
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: theme.textSecondary }}>Value Area %</span>
                        <InputNumber
                          size="small"
                          min={0.5}
                          max={0.9}
                          step={0.05}
                          value={volumeProfileValueAreaPct}
                          onChange={(value) =>
                            setVolumeProfileValueAreaPct(
                              clampNumber(value, 0.5, 0.9, volumeProfileValueAreaPct)
                            )
                          }
                        />
                      </div>
                    </div>
                  }
                >
                  <Button size="small" type="text" icon={<SlidersOutlined />} style={{ padding: 0, width: 20 }} />
                </Popover>
              ) : null;

            const placementMenu = buildPlacementMenu(item.id as IndicatorId, inPane);
            const placementToggle = (
              <Dropdown menu={placementMenu} trigger={['click']} placement="bottomRight">
                <Button
                  size="small"
                  type="text"
                  icon={<SettingOutlined />}
                  style={{ padding: 0, width: 20 }}
                />
              </Dropdown>
            );

            const deleteButton =
              onIndicatorToggle ? (
                <Tooltip title={`Remove ${item.label}`}>
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => onIndicatorToggle(item.id as IndicatorId)}
                    style={{ padding: 0, width: 20 }}
                  />
                </Tooltip>
              ) : null;

            return (
              <div
                key={item.id}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: `1px solid ${theme.surfaceBorder}`,
                  background: theme.surfaceBackground,
                  paddingRight: 8,
                  minWidth: 120,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    background: item.color,
                  }}
                />
              <span style={{ color: theme.textPrimary, fontSize: 12 }}>{item.label}</span>
                {!inPane && (
                  <Space size={4}>
                    {placementToggle}
                    {volumeProfileSettings}
                    {deleteButton}
                  </Space>
                )}
                {inPane && (
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {placementToggle}
                    {volumeProfileSettings}
                    {deleteButton}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {overlayLegend.filter((item) => item.placement === 'pane').length > 0 && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>Overlay (separate panes)</span>
          </div>
          {overlayLegend
            .filter((item) => item.placement === 'pane')
            .map((item) => {
              const placementToggle = (
                <Dropdown
                  menu={buildPlacementMenu(item.id as IndicatorId, true)}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <Button
                    size="small"
                    type="text"
                    icon={<SettingOutlined />}
                    style={{ padding: 0, width: 20 }}
                  />
                </Dropdown>
              );
              const deleteButton =
                onIndicatorToggle ? (
                  <Tooltip title={`Remove ${item.label}`}>
                    <Button
                      size="small"
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => onIndicatorToggle(item.id as IndicatorId)}
                      style={{ padding: 0, width: 20 }}
                    />
                  </Tooltip>
                ) : null;
              const volumeProfileSettings =
                item.id === 'volumeProfile' && showVolumeProfile ? (
                  <Popover
                    trigger="click"
                    placement="bottomRight"
                    content={
                      <div style={{ display: 'grid', gap: 8, minWidth: 220 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: theme.textSecondary }}>Price Buckets</span>
                          <InputNumber
                            size="small"
                            min={10}
                            max={100}
                            value={volumeProfileBuckets}
                            onChange={(value) =>
                              setVolumeProfileBuckets(
                                clampNumber(value, 10, 100, volumeProfileBuckets)
                              )
                            }
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: theme.textSecondary }}>Width %</span>
                          <InputNumber
                            size="small"
                            min={0.1}
                            max={0.5}
                            step={0.05}
                            value={volumeProfileWidthPct}
                            onChange={(value) =>
                              setVolumeProfileWidthPct(
                                clampNumber(value, 0.1, 0.5, volumeProfileWidthPct)
                              )
                            }
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: theme.textSecondary }}>Value Area %</span>
                          <InputNumber
                            size="small"
                            min={0.5}
                            max={0.9}
                            step={0.05}
                            value={volumeProfileValueAreaPct}
                            onChange={(value) =>
                              setVolumeProfileValueAreaPct(
                                clampNumber(value, 0.5, 0.9, volumeProfileValueAreaPct)
                              )
                            }
                          />
                        </div>
                      </div>
                    }
                  >
                    <Button size="small" type="text" icon={<SlidersOutlined />} style={{ padding: 0, width: 20 }} />
                  </Popover>
                ) : null;

              return (
                <div key={`pane-${item.id}`} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Space size={6}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: 5,
                          background: item.color,
                        }}
                      />
                      <span style={{ color: theme.textPrimary, fontSize: 12 }}>{item.label}</span>
                    </Space>
                    <Space size={4}>
                      {placementToggle}
                      {volumeProfileSettings}
                      {deleteButton}
                    </Space>
                  </div>
                  <div
                    ref={(el) => {
                      overlayPaneRefs.current[item.id as IndicatorId] = el;
                    }}
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: subHeight,
                      border: `1px solid ${theme.surfaceBorder}`,
                      borderRadius: '6px',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Crosshair lines for pane chart */}
                    {sharedCrosshairLogical !== null && overlayPaneCharts.current[item.id as IndicatorId] && (
                      (() => {
                        const chart = overlayPaneCharts.current[item.id as IndicatorId];
                        const x = chart?.timeScale().logicalToCoordinate(sharedCrosshairLogical as any);
                        const value = getOverlayValueAtTime(item.id as IndicatorId, sharedCrosshairTime ?? 0);
                        const series = getOverlaySeries(item.id as IndicatorId);
                        const y = value !== null ? series?.priceToCoordinate(value) : null;
                        const showVertical = typeof x === 'number' && x >= 0 && x <= getActualWidth();
                        const showHorizontal = typeof y === 'number';
                        return (
                          <>
                            {showVertical && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  bottom: 0,
                                  left: x,
                                  width: 1,
                                  background: theme.textSecondary,
                                  opacity: 0.4,
                                  pointerEvents: 'none',
                                  zIndex: 3,
                                }}
                              />
                            )}
                            {showHorizontal && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  top: y as number,
                                  height: 1,
                                  background: theme.textSecondary,
                                  opacity: 0.4,
                                  pointerEvents: 'none',
                                  zIndex: 3,
                                }}
                              />
                            )}
                          </>
                        );
                      })()
                    )}
                    {sharedCrosshairTime !== null && (
                      (() => {
                        const value = getOverlayValueAtTime(item.id as IndicatorId, sharedCrosshairTime);
                        if (value === null || Number.isNaN(value)) return null;
                        const series = getOverlaySeries(item.id as IndicatorId);
                        const y = series?.priceToCoordinate(value);
                        return (
                          <div
                            style={{
                              position: 'absolute',
                              top: typeof y === 'number' ? y - 14 : 8,
                              right: 8 + Math.max(priceScaleWidthRef.current, 36),
                              background: theme.surfaceBackground,
                              color: theme.textPrimary,
                              border: `1px solid ${theme.surfaceBorder}`,
                              borderRadius: 6,
                              padding: '2px 8px',
                              fontSize: 12,
                              zIndex: 4,
                              pointerEvents: 'none',
                            }}
                          >
                            {value.toFixed(2)}
                          </div>
                        );
                      })()
                    )}
                    {sharedCrosshairTime !== null && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 6,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: theme.surfaceBackground,
                          color: theme.textSecondary,
                          border: `1px solid ${theme.surfaceBorder}`,
                          borderRadius: 6,
                          padding: '2px 8px',
                          fontSize: 11,
                          zIndex: 4,
                          pointerEvents: 'none',
                        }}
                      >
                        {formatTimestampToEST(sharedCrosshairTime)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {showVolume && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              Volume{' '}
              {ohlcData.length > 0 && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  {Number(ohlcData[ohlcData.length - 1]?.volume || 0).toLocaleString()}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove Volume">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('volume')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={volumeChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showWilliamsR && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              Williams %R (14){' '}
              {typeof latestWilliams === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestWilliams.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove Williams %R">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('williamsR')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={williamsChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showMacd && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              MACD (12,26,9){' '}
              {latestMacd && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  MACD: {latestMacd.macd?.toFixed(2) ?? '--'} | Signal: {latestMacd.signal?.toFixed(2) ?? '--'}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove MACD">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('macd')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={macdChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showObv && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              OBV{' '}
              {obvData.length > 0 && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  {Number(obvData[obvData.length - 1]?.value ?? 0).toFixed(2)}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove OBV">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('obv')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={obvChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showAdl && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              ADL{' '}
              {typeof latestAdl === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  {Number(latestAdl).toFixed(2)}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove ADL">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('adl')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={adlChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showAdx && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              ADX (14){' '}
              {latestAdx && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  ADX: {latestAdx.adx?.toFixed(2) ?? '--'} | +DI: {latestAdx.pdi?.toFixed(2) ?? '--'} | -DI: {latestAdx.mdi?.toFixed(2) ?? '--'}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove ADX">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('adx')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={adxChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showAo && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              AO (5,34){' '}
              {typeof latestAo === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestAo.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove AO">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('ao')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={aoChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showCci && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              CCI (20){' '}
              {typeof latestCci === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestCci.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove CCI">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('cci')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={cciChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showForceIndex && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              Force Index (13){' '}
              {typeof latestFi === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestFi.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove Force Index">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('forceIndex')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={fiChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showKst && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              KST (10,15,20,30){' '}
              {latestKst && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  KST: {latestKst.kst?.toFixed(2) ?? '--'} | Signal: {latestKst.signal?.toFixed(2) ?? '--'}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove KST">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('kst')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={kstChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showAtr && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              ATR (14){' '}
              {typeof latestAtr === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestAtr.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove ATR">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('atr')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={atrChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showMfi && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              MFI (14){' '}
              {typeof latestMfi === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestMfi.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove MFI">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('mfi')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={mfiChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showStochastic && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              Stochastic (14,3){' '}
              {latestStochastic && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  %K: {latestStochastic.k?.toFixed(2) ?? '--'} | %D: {latestStochastic.d?.toFixed(2) ?? '--'}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove Stochastic">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('stochastic')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={stochasticChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showStochRsi && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              StochRSI (14,14,3,3){' '}
              {latestStochRsi && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>
                  %K: {latestStochRsi.k?.toFixed(2) ?? '--'} | %D: {latestStochRsi.d?.toFixed(2) ?? '--'}
                </span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove StochRSI">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('stochRsi')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={stochRsiChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showRoc && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              ROC (14){' '}
              {typeof latestRoc === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestRoc.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove ROC">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('roc')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={rocChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {showTrix && (
        <div style={{ width: getActualWidth(), marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              TRIX (15){' '}
              {typeof latestTrix === 'number' && (
                <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{latestTrix.toFixed(2)}</span>
              )}
            </span>
            {onIndicatorToggle && (
              <Tooltip title="Remove TRIX">
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => onIndicatorToggle('trix')}
                />
              </Tooltip>
            )}
          </div>
          <div
            ref={trixChartRef}
            style={{
              width: '100%',
              height: subHeight,
              border: `1px solid ${theme.surfaceBorder}`,
              borderRadius: '6px',
              overflow: 'hidden'
            }}
          />
        </div>
      )}

      {/* Tooltip for candlestick hover */}
      {tooltip.visible && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x + 10,
            top: tooltip.y - 10,
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontFamily: 'monospace',
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            minWidth: '200px'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {tooltip.time}
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#ccc' }}>O:</span> {tooltip.ohlc.open.toFixed(2)}
            <span style={{ color: '#ccc', marginLeft: '8px' }}>H:</span> {tooltip.ohlc.high.toFixed(2)}
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#ccc' }}>L:</span> {tooltip.ohlc.low.toFixed(2)}
            <span style={{ color: '#ccc', marginLeft: '8px' }}>C:</span> {tooltip.ohlc.close.toFixed(2)}
          </div>
          <div style={{ fontWeight: 'bold', color: tooltip.ohlc.close >= tooltip.ohlc.open ? '#4ade80' : '#f87171' }}>
            Price: ${tooltip.price}
          </div>
        </div>
      )}
    </div>
  );
});

export default MultiPaneChart;
